import PhysicalExplorer from "./components/PhysicalExplorer";
import { diagramExplorerLabel } from "../shared/explorer";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AlertCircle,
  ArrowDownToLine,
  ArrowRight,
  ArrowUpFromLine,
  Bell,
  BookOpen,
  Boxes,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  CirclePlus,
  Columns3,
  Database,
  Ellipsis,
  Eye,
  EyeOff,
  Minus,
  FileCode2,
  FilePlus2,
  Folder,
  GitBranch,
  GitCompareArrows,
  GitFork,
  History,
  LayoutGrid,
  LoaderCircle,
  LogOut,
  Maximize2,
  Minimize2,
  ScanLine,
  MousePointer2,
  Network,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  Palette,
  Plus,
  Redo2,
  RefreshCw,
  Save,
  Search,
  Settings2,
  Shield,
  SlidersHorizontal,
  Sparkles,
  Table2,
  Trash2,
  Undo2,
  X,
} from "lucide-react";
import type {
  Command,
  CommandReceipt,
  Conflict,
  DomainObject,
  Diagram,
  ModelDocument,
  ModelSnapshot,
  ModelSummary,
  Operation,
  Preferences,
  Repository,
  Session,
} from "../shared/contracts";
import { defaultPreferences } from "../shared/contracts";
import { WorkspaceCommand, CustomizePalettesButton, WorkspaceToolsProvider, WorkspaceHeader, WorkspaceMenuButton, PaletteVisibilityControls, DockableToolbar, DockSlot, TypographyTools, PanelResizeHandle } from "./components/WorkspaceTools";
import { vocabulary, relationshipSuggestion, RELATIONSHIP_SUGGESTION_VERSION } from "../shared/entityPresentation";
import { getThemeTokens, readableText, type AppearanceTarget } from "../shared/appearance";
import { interfaceScaleForPreferences, normalizeInterfaceScale, startupWorkspaceLayout, clampPanelWidth } from "../shared/workspace";
import { PRODUCT_RELEASE } from "../shared/version";
import AppearanceDialog from "./components/AppearanceDialog";
import ModelTypeIcon from "./components/ModelTypeIcon";
import WorkspacePropertiesDialog from "./components/WorkspacePropertiesDialog";
import {
  applyOperations,
  promoteViewToSubjectArea,
  createObject,
  defaultEntityFields,
  operationConflicts,
  uid,
  validateModel,
} from "../core";
import { api, captureSession } from "./api";
import { ViewMembershipDialog } from "./components/ViewMembershipDialog";
import ImportReviewDialog from "./components/ImportReviewDialog";
import RelationshipIcon from "./components/RelationshipIcon";
import { prepareDiagramOperations, prepareDiagramPreferenceOperations, measuredDiagramBoxes } from "./components/prepareDiagramOperations";
import ModelCanvas from "./components/ModelCanvas";
import { acknowledgeModelSave, modelSaveRequiresReconciliation } from "./modelPersistence";
import ObjectInspector from "./components/ObjectInspector";
import AdminPanel from "./panels/AdminPanel";
import AssistantPanel from "./panels/AssistantPanel";
import HelpPanel from "./panels/HelpPanel";
import PerspectivePanel from "./panels/PerspectivePanel";
import {
  BlocksDialog,
  ExportDialog,
  HistoryDialog,
  ImportDialog,
  NamingDialog,
  NewModelDialog,
  PreferencesDialog,
  SelectionDialog,
  TransformDialog,
  dialects,
} from "./components/WorkbenchDialogs";
import {
  Button,
  Diagnostics,
  EmptyState,
  Field,
  IconButton,
  Modal,
  Notice,
  dialectLabel,
  downloadText,
  errorText,
  modelTypeLabel,
} from "./components/Primitives";

type Batch = { operations: Operation[]; summary: string; inverse?: Operation[] };
type UndoState = { canUndo: boolean; canRedo: boolean; undoSummary?: string; redoSummary?: string };
type Dialog =
  | "new-model"
  | "import"
  | "export"
  | "blocks"
  | "transform"
  | "naming"
  | "preferences"
  | "history"
  | "admin"
  | "database"
  | "new-repository"
  | "selection"
  | null;
function Brand({
  large = false,
  name = "DataGlow",
}: {
  large?: boolean;
  name?: string;
}) {
  return (
    <div className={`brand ${large ? "large" : ""}`}>
      <svg viewBox="0 0 34 34" role="img" aria-label="DataGlow logo">
        <path
          d="M6 10 17 4l11 6v14l-11 6L6 24Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
        />
        <path
          d="m6 10 11 6 11-6M17 16v14M6 24l11-8 11 8"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
        />
        <circle cx="17" cy="16" r="3.4" fill="currentColor" />
        <circle cx="6" cy="10" r="2.2" fill="currentColor" />
        <circle cx="28" cy="10" r="2.2" fill="currentColor" />
      </svg>
      <span>
        {name === "DataGlow" ? (
          <>
            Data<span>Glow</span>
          </>
        ) : (
          name
        )}
      </span>
      {large && <small>ER DESIGNER</small>}
    </div>
  );
}
export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [requiresToken, setRequiresToken] = useState(false);
  const [bootError, setBootError] = useState("");
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const result: any = await api("/auth/session");
        if (alive) {
          const s = result.session || result;
          captureSession(s);
          setSession(s);
        }
      } catch {
        try {
          const status: any = await api("/setup/status");
          if (alive) {
            setNeedsSetup(status.needsSetup);
            setRequiresToken(status.requiresSetupToken);
          }
        } catch (e) {
          if (alive) setBootError(errorText(e));
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);
  if (loading)
    return (
      <div className="boot-screen">
        <Brand large />
        <LoaderCircle className="spin" size={23} />
        <p>Opening your workspace…</p>
      </div>
    );
  if (!session)
    return (
      <Authentication
        setup={needsSetup}
        requiresToken={requiresToken}
        initialError={bootError}
        onSession={(s) => {
          captureSession(s);
          setSession(s);
        }}
      />
    );
  return (
    <Workspace
      session={session}
      onSession={(s) => {
        captureSession(s);
        setSession(s);
      }}
      onLogout={() => setSession(null)}
    />
  );
}
function Authentication({
  setup,
  requiresToken,
  onSession,
  initialError,
}: {
  setup: boolean;
  requiresToken: boolean;
  onSession: (s: Session) => void;
  initialError: string;
}) {
  const [form, setForm] = useState({
    organizationCode: "",
    organizationName: "",
    name: "",
    email: "",
    password: "",
    setupToken: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(initialError);
  const [passwordVisible, setPasswordVisible] = useState(false);
  useEffect(() => setPasswordVisible(false), [setup]);
  const field = (k: keyof typeof form) => ({
    value: form[k],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm({ ...form, [k]: e.target.value }),
  });
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r: any = await api(setup ? "/setup/bootstrap" : "/auth/login", {
        method: "POST",
        body: JSON.stringify(form),
      });
      onSession(r.session || r);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="auth-page">
      <aside className="auth-story">
        <Brand large />
        <div className="auth-story-copy">
          <span className="eyebrow">
            FROM BUSINESS MEANING TO DATABASE DESIGN
          </span>
          <h1>
            Make complex
            <br />
            data feel
            <br />
            <em>connected.</em>
          </h1>
          <p>
            A thoughtful workspace for modeling systems, preserving meaning, and
            building with confidence.
          </p>
        </div>
        <div className="auth-model-art" aria-hidden="true">
          <div className="art-table">
            <span>
              <KeyIcon />
              Customer
            </span>
            <i>
              customer_id <b>uuid</b>
            </i>
            <i>
              display_name <b>text</b>
            </i>
            <i>
              created_at <b>date</b>
            </i>
          </div>
          <div className="art-connector" />
          <div className="art-table small">
            <span>
              <Table2 size={13} />
              Order
            </span>
            <i>
              order_id <b>uuid</b>
            </i>
            <i>
              customer_id <b>uuid</b>
            </i>
          </div>
        </div>
        <div className="auth-caption">
          <span className="status-dot" />
          Your models. Your environment. Your control.
        </div>
      </aside>
      <main className="auth-form-area">
        <div className="auth-form-card">
          <div className="auth-badge">{PRODUCT_RELEASE.toUpperCase()} · DEVELOPMENT WORKSPACE</div>
          <h2>{setup ? "Create your studio" : "Welcome to your studio"}</h2>
          <p>
            {setup
              ? "Set up the first organization and its administrator."
              : "Sign in to continue your modeling work."}
          </p>
          {error && <Notice kind="error">{error}</Notice>}
          <form onSubmit={submit}>
            {setup && (
              <div className="form-row">
                <Field label="Your name">
                  <input required autoComplete="name" {...field("name")} />
                </Field>
                <Field label="Organization name">
                  <input
                    required
                    autoComplete="organization"
                    {...field("organizationName")}
                  />
                </Field>
              </div>
            )}
            <Field
              label="Organization code"
              hint="The code identifies your studio. Your account still controls access."
            >
              <input
                required
                autoComplete="organization"
                placeholder="Your organization code"
                {...field("organizationCode")}
              />
            </Field>
            <Field label="Email address">
              <input
                type="email"
                required
                autoComplete="username"
                placeholder="you@organization.com"
                {...field("email")}
              />
            </Field>
            <Field
              label="Password"
              hint={
                setup
                  ? "Choose at least 12 characters. No default password is provided."
                  : undefined
              }
            >
              <div className="password-input">
                <input
                  type={passwordVisible ? "text" : "password"}
                  aria-label="Password"
                  minLength={setup ? 12 : undefined}
                  required
                  autoComplete={setup ? "new-password" : "current-password"}
                  {...field("password")}
                />
                <button type="button" aria-label={passwordVisible ? "Hide password" : "Show password"} aria-pressed={passwordVisible} title={passwordVisible ? "Hide password" : "Show password"} onClick={() => setPasswordVisible(!passwordVisible)}>{passwordVisible ? <EyeOff size={18}/> : <Eye size={18}/>}</button>
              </div>
            </Field>
            {setup && requiresToken && (
              <Field
                label="Deployment setup token"
                hint="Use the token supplied by the installation operator."
              >
                <input
                  type="password"
                  required
                  autoComplete="off"
                  {...field("setupToken")}
                />
              </Field>
            )}
            <Button type="submit" variant="primary full-width" busy={busy}>
              {setup ? "Create studio" : "Open workspace"}
              <ArrowRight size={16} />
            </Button>
          </form>
          <p className="auth-fine-print">
            {setup
              ? "First-use setup is available only while the installation has no organization."
              : "Need access? Contact your studio administrator."}
          </p>
        </div>
        <div className="auth-footer">
          Designed for the details.<span>DataGlow ER Designer</span>
        </div>
      </main>
    </div>
  );
}
function KeyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <circle cx="5" cy="5" r="3" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="m7.5 7.5 6 6m-2-2 2-2m-4 0 2-2"
        stroke="currentColor"
        strokeWidth="1.4"
      />
    </svg>
  );
}
function Workspace({
  session,
  onSession,
  onLogout,
}: {
  session: Session;
  onSession: (s: Session) => void;
  onLogout: () => void;
}) {
  const [repositories, setRepositories] = useState<Repository[]>([]);
  const [serverReachable, setServerReachable] = useState<boolean | null>(null);
  const [models, setModels] = useState<ModelSummary[]>([]);
  const [repositoryId, setRepositoryId] = useState("");
  const [snapshot, setSnapshot] = useState<ModelSnapshot | null>(null);
  const [draft, setDraft] = useState<ModelDocument | null>(null);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [redoBatches, setRedoBatches] = useState<Batch[]>([]);
  const [undoState, setUndoState] = useState<UndoState>({ canUndo: false, canRedo: false });
  const [serverUndoDepth, setServerUndoDepth] = useState(0);
  const [repositoryRedoInvalidated, setRepositoryRedoInvalidated] = useState(false);
  const [helpOverlayTopic, setHelpOverlayTopic] = useState<string | null>(null);
  const historyRequest = useRef<{ action: "undo" | "redo"; requestId: string; modelId: string; baseRevision: number } | null>(null);
  const [propertyTarget, setPropertyTarget] = useState<AppearanceTarget | null>(null);
  const [appearanceTarget, setAppearanceTarget] = useState<AppearanceTarget | null>(null);
  const [propertyDirty, setPropertyDirty] = useState(false);
  const [inspectorGeneration, setInspectorGeneration] = useState(0);
  const [preferences, setPreferences] =
    useState<Preferences>(defaultPreferences);
  const localHistory = useRef<{past: Batch[]; future: Batch[]; owned: boolean; persisted: boolean}>({past: [], future: [], owned:false, persisted:false});
  const [preferencesReady, setPreferencesReady] = useState(false);
  const [interfaceScale, setInterfaceScale] = useState(1);
  useEffect(() => {
    if (preferencesReady) setInterfaceScale(interfaceScaleForPreferences(preferences));
  }, [preferencesReady, preferences.startupInterfaceSize, preferences.interfaceScale]);
  const scaleChange = useRef<(value:number)=>void>(()=>{});
  const scaleRef = useRef(interfaceScale); scaleRef.current = interfaceScale;
  useEffect(() => {
    document.documentElement.style.setProperty("--ui-scale", String(interfaceScale));
    return () => { document.documentElement.style.removeProperty("--ui-scale"); };
  }, [interfaceScale]);
  useEffect(() => {
    const adjust = (event: WheelEvent) => {
      const element = event.target instanceof Element ? event.target : null;
      if (!element?.closest(".app-header") || element.closest("input,textarea,select,[contenteditable=true]") || event.ctrlKey || event.metaKey || !event.deltaY) return;
      event.preventDefault();
      scaleChange.current(normalizeInterfaceScale(scaleRef.current + (event.deltaY < 0 ? .1 : -.1)));
    };
    window.addEventListener("wheel", adjust, {passive:false});
    return () => window.removeEventListener("wheel", adjust);
  }, []);
  const [selection, setSelection] = useState<string[]>([]);
  const [diagramId, setDiagramId] = useState("");
  const [tabs, setTabs] = useState<ModelSummary[]>([]);
  const [rightPanel, setRightPanel] = useState("inspector");
  const [helpTopic, setHelpTopic] = useState("getting-started");
  const [viewRequest, setViewRequest] = useState<{mode:"subject-area"|"view"|"add";sourceDiagramId:string;targetDiagramId?:string;parentDiagramId?:string;selection?:string[]} | null>(null);
  const [explorerWidth, setExplorerWidth] = useState<number | null>(null);
  const [temporaryPropertiesWidth, setTemporaryPropertiesWidth] = useState<number | null>(null);
  const [viewportWidth,setViewportWidth] = useState(window.innerWidth);
  useEffect(()=>{const resize=()=>setViewportWidth(window.innerWidth);window.addEventListener("resize",resize);return()=>window.removeEventListener("resize",resize);},[]);
  useEffect(()=>setTemporaryPropertiesWidth(null),[selection.join(),diagramId,rightPanel]);
  const [dialog, setDialog] = useState<Dialog>(null);

  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recentlySaved, setRecentlySaved] = useState(false);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [canvasGestureActive, setCanvasGestureActive] = useState(false);
  const canvasGesture = useRef(false);
  const gestureWaiters = useRef<(() => void)[]>([]);
  const markCanvasGesture = useCallback((active: boolean) => {
    canvasGesture.current = active; setCanvasGestureActive(active);
    if (!active) { const waiting = gestureWaiters.current; gestureWaiters.current = []; waiting.forEach(resolve => resolve()); }
  }, []);
  const composing = useRef(false);
  useEffect(() => {
    const start = () => { composing.current = true; };
    const end = () => { composing.current = false; setFormEditRevision(value => value + 1); };
    document.addEventListener("compositionstart", start); document.addEventListener("compositionend", end);
    return () => { document.removeEventListener("compositionstart", start); document.removeEventListener("compositionend", end); clearTimeout(savedTimer.current); };
  }, []);
  const [autosaveBlocked, setAutosaveBlocked] = useState(false);
  const [toast, setToast] = useState<{
    text: string;
    kind: "info" | "error" | "success";
  } | null>(null);
  const [search, setSearch] = useState("");
  const [focus, setFocus] = useState(false);
  const [navOpen, setNavOpen] = useState(window.innerWidth > 850);
  const [dockOpen, setDockOpen] = useState(window.innerWidth > 850);
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [canvasCommand, setCanvasCommand] = useState<any>();
  const canvasPlacement = useRef<((size:{width:number;height:number}, preferred?:{x:number;y:number})=>{x:number;y:number})|null>(null);
  const registerCanvasPlacement = useCallback((resolver:typeof canvasPlacement.current)=>{canvasPlacement.current=resolver;},[]);
  const [addObject, setAddObject] = useState<{
    ownerId?: string;
    kind?: string;
    position?: {x:number;y:number};
  } | null>(null);
  const [addRelationship, setAddRelationship] = useState<{
    sourceId?: string;
    targetId?: string;
  } | null>(null);
  const [relationshipPicking, setRelationshipPicking] = useState(false);
  const [relationshipSource, setRelationshipSource] = useState<string | null>(null);
  const [presentationLocks, setPresentationLocks] = useState<string[]>([]);
  useEffect(() => { api("/administration/appearance-policy").then((policy: any) => setPresentationLocks(policy.locked || [])).catch(() => {}); }, []);
  const [confirm, setConfirm] = useState<{
    title: string;
    message: string;
    label: string;
    action: () => void;
  } | null>(null);
  const [remoteRevision, setRemoteRevision] = useState(0);
  const [conflictOpen, setConflictOpen] = useState(false);
  const unresolvedAcknowledgement = useRef(false);
  const [conflict, setConflict] = useState<{
    incoming: ModelSnapshot;
    conflicts: Conflict[];
  } | null>(null);
  const [inspectorDirty, setInspectorDirty] = useState(false);
  const [connection, setConnection] = useState<"online" | "retrying">("online");
  const [repositoryAccess, setRepositoryAccess] = useState<{id:string; phase:"connecting"|"connected"|"reconnecting"|"unavailable"; name?:string}>({id:"",phase:"connecting"});
  const [preferenceSaveError, setPreferenceSaveError] = useState("");
  const [preferenceSaveState, setPreferenceSaveState] = useState<"idle" | "pending" | "saving" | "saved" | "error">("idle");
  const [saveFailure, setSaveFailure] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const refreshInFlight = useRef(false);
  const saveInFlight = useRef<Promise<boolean> | null>(null);
  const [navigationGuard, setNavigationGuard] = useState<{ action: () => void } | null>(null);
  const [navigationSaving, setNavigationSaving] = useState(false);
  const navigationInFlight = useRef(false);
  const [formEditRevision, setFormEditRevision] = useState(0);
  const inspectorSave = useRef<((report?: boolean) => boolean) | null>(null);
  const propertySave = useRef<((report?: boolean) => boolean) | null>(null);
  const registerInspectorSave = useCallback((handler: ((report?: boolean) => boolean) | null) => { inspectorSave.current = handler; }, []);
  const registerPropertySave = useCallback((handler: ((report?: boolean) => boolean) | null) => { propertySave.current = handler; }, []);
  const markInspectorDirty = useCallback((dirty: boolean) => { setInspectorDirty(dirty); if (dirty) setFormEditRevision(n => n + 1); }, []);
  const markPropertyDirty = useCallback((dirty: boolean) => { setPropertyDirty(dirty); if (dirty) setFormEditRevision(n => n + 1); }, []);
  const modelOpenRequest = useRef(0);
  const modelLoadInFlight = useRef(false);
  useEffect(() => {
    let cancelled=false, inFlight=false;
    const id=repositoryId;
    setRepositoryAccess({id,phase:"connecting"});
    setConnection("retrying");
    if(!id)return;
    const verify=async () => {
      if(inFlight)return;
      inFlight=true;
      setRepositoryAccess(old=>old.id===id&&old.phase==="unavailable"?{...old,phase:"reconnecting"}:old);
      try { const repo=await api<Repository>(`/repositories/${encodeURIComponent(id)}`); if(!cancelled)setRepositoryAccess({id,phase:"connected",name:repo.name}); }
      catch { if(!cancelled)setRepositoryAccess(old=>({id,phase:"unavailable",name:old.id===id?old.name:undefined})); }
      finally {inFlight=false;}
    };
    void verify();const interval=setInterval(()=>void verify(),15000);
    return()=>{cancelled=true;clearInterval(interval);};
  },[repositoryId]);
  const [context, setContext] = useState<{
    x: number;
    y: number;
    id: string;
    kind: AppearanceTarget["kind"] | "explorer" | "models" | "diagrams" | "objects" | "relationships" | "module";
  } | null>(null);
  const navigatorMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!context) return;
    const prior = document.activeElement as HTMLElement | null;
    navigatorMenuRef.current?.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus();
    const keyboard = (event: KeyboardEvent) => {
      if(event.key === "Escape") {event.preventDefault();setContext(null);prior?.focus();return;}
      if(!["ArrowDown","ArrowUp","Home","End"].includes(event.key)) return;
      event.preventDefault();
      const items = [...(navigatorMenuRef.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)") || [])];
      if(!items.length) return;
      const current = items.indexOf(document.activeElement as HTMLButtonElement);
      const next = event.key === "Home" ? 0 : event.key === "End" ? items.length - 1 : (current + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length;
      items[next]?.focus();
    };
    window.addEventListener("keydown",keyboard);
    return () => window.removeEventListener("keydown",keyboard);
  }, [context]);
  const pending = batches.flatMap((b) => b.operations);
  const pendingCount = pending.length;
  const saveCommand = useRef<Command | null>(null);
  const savedBatches = useRef<Batch[]>([]);
  const savedDocument = useRef<ModelDocument | null>(null);
  const stateRef = useRef({
    snapshot,
    draft,
    batches,
    preferences,
    saving,
    inspectorDirty: inspectorDirty || propertyDirty,
  });
  stateRef.current = {
    snapshot,
    draft,
    batches,
    preferences,
    saving,
    inspectorDirty: inspectorDirty || propertyDirty,
  };
  const busyPoll = useRef(false);
  const developer =
    session.permissions.includes("column.add") &&
    !session.permissions.includes("model.edit");
  const canEdit = session.permissions.includes("model.edit");
  const canDiagramEdit = canEdit || session.permissions.includes("diagram.edit");
  const canChange = canDiagramEdit || developer;
  const canCreate = session.permissions.includes("model.create");
  const canExport = session.permissions.includes("model.export");
  const canPublish = session.permissions.includes("model.publish");
  const canAdmin = session.permissions.some((p) =>
    [
      "platform.manage",
      "users.manage",
      "roles.manage",
      "studio.configure",
      "api.configure",
      "service.manage",
      "assistant.configure",
      "tenant.transfer",
    ].includes(p),
  );
  const readOnly = !canEdit && !developer;
  const notify = (text: string, kind: "info" | "error" | "success" = "info") =>
    setToast({ text, kind });
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(
      () => setToast(null),
      toast.kind === "error" ? 10000 : 4500,
    );
    return () => clearTimeout(timer);
  }, [toast]);
  const refreshLists = async () => {
    const results = await Promise.all([
      api<any>("/repositories"),
      api<any>("/models"),
    ]);
    setServerReachable(true);
    setRepositories(
      Array.isArray(results[0]) ? results[0] : results[0].repositories || [],
    );
    setModels(Array.isArray(results[1]) ? results[1] : results[1].models || []);
    setRepositoryId(
      (prev) =>
        prev ||
        (Array.isArray(results[0])
          ? results[0][0]?.repositoryId
          : results[0].repositories?.[0]?.repositoryId) ||
        "",
    );
  };
  useEffect(() => {
    refreshLists().catch((e) => { setServerReachable(false); notify(errorText(e), "error"); });
    api<any>("/preferences")
      .then((p) => {
        const loaded = { ...defaultPreferences, ...(p.preferences || p) };
        setPreferences({ ...loaded, workspaceLayout: startupWorkspaceLayout(loaded) });
        setPreferencesReady(true);
      })
      .catch((e) =>
        notify(
          "Personal preferences could not be loaded. Refresh to retry. " +
            errorText(e),
          "error",
        ),
      );
  }, []);
  const [systemDark, setSystemDark] = useState(() => window.matchMedia("(prefers-color-scheme: dark)").matches);
  useEffect(() => {
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const update = () => setSystemDark(query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  const theme = preferences.theme === "system" ? systemDark ? "dark" : "light" : preferences.theme;
  useEffect(() => {
    const tokens = getThemeTokens(preferences, systemDark);
    const style = document.documentElement.style;
    document.documentElement.dataset.theme = theme;
    Object.entries(tokens).forEach(([name, value]) => style.setProperty(`--${name}`, value));
    style.setProperty("--accent-text", readableText(tokens.accent));
    style.setProperty("--ink", tokens.text);
    style.setProperty("--ink-light", tokens.muted);
    style.setProperty("--panel", `color-mix(in srgb, ${tokens.surface} 84%, ${tokens.canvas})`);
    style.setProperty("--hover", `color-mix(in srgb, ${tokens.accent} 8%, ${tokens.surface})`);
    style.setProperty("--accent-soft", `color-mix(in srgb, ${tokens.accent} 12%, ${tokens.surface})`);
    style.setProperty("--font-family", preferences.fontFamily);
    style.setProperty("--font-size", `${preferences.fontSize}px`);
    style.setProperty("--title-size", `${preferences.titleSize}px`);
  }, [preferences, theme, systemDark]);
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (stateRef.current.batches.length || stateRef.current.inspectorDirty || Object.keys(pendingPreferences.current).length || preferenceQueue.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);
  const acceptSnapshot = (s: ModelSnapshot) => {
    unresolvedAcknowledgement.current = false;
    localHistory.current = {past: [], future: [], owned:false, persisted:false};
    savedBatches.current = [];
    setRecentlySaved(false);
    stateRef.current = { ...stateRef.current, snapshot: s, draft: s.document, batches: [], inspectorDirty: false };
    setSaveFailure(false);
    setSnapshot(s);
    setDraft(s.document);
    setBatches([]);
    setRedoBatches([]);
    setServerUndoDepth(0);
    setRepositoryRedoInvalidated(false);
    setPropertyTarget(null);
    setAppearanceTarget(null);
    setPropertyDirty(false);
    setInspectorGeneration((value) => value + 1);
    setSelection([]);
    setInspectorDirty(false);
    setRemoteRevision(0);
    saveCommand.current = null;
    setDiagramId((prev) =>
      s.document.diagrams.some((d) => d.diagramId === prev)
        ? prev
        : s.document.diagrams[0]?.diagramId || "",
    );
  };
  const openModel = async (id: string) => {
    void flushPreferences().catch(()=>{});
    const request=++modelOpenRequest.current;
    modelLoadInFlight.current = true;
    setBusy(true);
    try {
      const s: any = await api(`/models/${id}`);
      if(request!==modelOpenRequest.current)return false;
      if (stateRef.current.batches.length || stateRef.current.inspectorDirty || saveInFlight.current || historyRequest.current) {
        notify("The current model changed while opening another model. Your edits are retained; save them before switching.", "error");
        return false;
      }
      acceptSnapshot(s.snapshot || s);
      const summary = models.find((m) => m.modelId === id) || {
        modelId: id,
        name: (s.snapshot || s).document.model.name,
        repositoryId: (s.snapshot || s).repositoryId,
        branchId: (s.snapshot || s).branchId,
      };
      setTabs((prev) =>
        prev.some((t) => t.modelId === id)
          ? prev
          : [...prev, summary as ModelSummary],
      );
      setRepositoryId((s.snapshot || s).repositoryId);
      setRightPanel("inspector");
      setDockOpen(window.innerWidth > 850);
      setFocus(false);
      return true;
    } catch (e) {
      notify(errorText(e), "error");
      return false;
    } finally {
      if(request===modelOpenRequest.current) {modelLoadInFlight.current = false;setBusy(false);}
    }
  };
  const discardPending = () => {
    localHistory.current = {past: [], future: [], owned:false, persisted:false};
    const current = stateRef.current;
    stateRef.current = { ...current, draft: current.snapshot?.document || current.draft, batches: [], inspectorDirty: false };
    setDraft(current.snapshot?.document || current.draft);
    setBatches([]); setRedoBatches([]); setPropertyTarget(null); setAppearanceTarget(null);
    setPropertyDirty(false); setInspectorDirty(false); setInspectorGeneration(value => value + 1);
    setSaveFailure(false); setAutosaveBlocked(false); saveCommand.current = null;
  };
  const navigate = (action: () => void) => {
    void flushPreferences().catch(()=>{});
    if (navigationInFlight.current || navigationGuard || modelLoadInFlight.current) return;
    if (historyRequest.current) {
      notify("Resolve the unconfirmed Undo or Redo before leaving this model.", "error");
      return;
    }
    const current = stateRef.current;
    if (saveInFlight.current || (current.preferences.saveMode === "automatic" && (current.batches.length || current.inspectorDirty || saveCommand.current))) {
      navigationInFlight.current = true;
      setNavigationSaving(true);
      void save().then(ok => { if (ok) action(); }).finally(() => { navigationInFlight.current = false; setNavigationSaving(false); });
    } else if (current.batches.length || current.inspectorDirty || saveCommand.current) {
      setNavigationGuard({ action });
    } else action();
  };
  const apply = (operations: Operation[], summary: string, measurementPreferences?: Preferences) => {
    const s = stateRef.current;
    if (!s.draft || !operations.length) return false;
    if (historyRequest.current || modelLoadInFlight.current) {
      notify("Resolve the current save before making another change.", "error");
      return false;
    }
    try {
      operations = prepareDiagramOperations(s.draft, operations, measurementPreferences ?? s.preferences);
      const result = applyOperations(s.draft, operations);
      const batch = { operations, summary, inverse: result.inverse };
      const nextBatches = [...s.batches, batch];
      localHistory.current = {...localHistory.current, past: [...localHistory.current.past, batch], future: [], owned:true};
      stateRef.current = { ...s, draft: result.document, batches: nextBatches };
      setDraft(result.document);
      setAutosaveBlocked(false);
      setBatches(nextBatches);
      setRedoBatches([]);
      setServerUndoDepth(0);
      setRepositoryRedoInvalidated(true);
      const createdDiagrams = operations.filter(op => op.type === "diagram.create").map(op => op.diagram as Diagram);
      const created = createdDiagrams.find(d => d.representationId === result.document.model.activeRepresentation) ?? createdDiagrams[createdDiagrams.length - 1];
      if (created) {
        setDiagramId(created.diagramId); setSelection([]); setSearch("");
        setPreferences(previous => ({...previous, explorerSections:{...previous.explorerSections,diagrams:true}}));
        requestAnimationFrame(() => globalThis.document.querySelector(`[data-diagram-id="${created.diagramId}"]`)?.scrollIntoView({block:"nearest"}));
      }
      return true;
    } catch (e) {
      notify(errorText(e), "error");
      setDiagnosticsOpen(true);
      return false;
    }
  };
  const save = (automatic = false): Promise<boolean> => {
    if (saveInFlight.current) {
      if (automatic) return saveInFlight.current;
      return saveInFlight.current.then(ok => ok ? save(false) : false);
    }
    const run = async (): Promise<boolean> => {
    if (!canChange || historyRequest.current || modelLoadInFlight.current || (automatic && composing.current)) return false;
    if (unresolvedAcknowledgement.current) { if (!automatic) setConflictOpen(true); return false; }
    if (stateRef.current.inspectorDirty) {
      const handlers = [inspectorSave.current, propertySave.current].filter(Boolean);
      const completed = !!handlers.length && handlers.every(handler => handler!(!automatic));
      if (!completed && !automatic) {
        notify("Complete the highlighted property fields before saving. Your edits are retained.", "error");
        return false;
      }
      if (completed) {
        stateRef.current.inspectorDirty = false;
        setInspectorDirty(false); setPropertyDirty(false);
      }
      // An incomplete field remains local. It must not prevent background
      // persistence of unrelated operations that have already completed.
    }
    const s = stateRef.current;
    if (!s.snapshot) return false;
    if (!s.batches.length && !saveCommand.current) return true;
    stateRef.current.saving = true;
    setSaving(true);
    const ops = s.batches.flatMap((b) => b.operations);
    const command: Command = saveCommand.current || {
      commandSchemaVersion: "1.0",
      commandId: uid(),
      modelId: s.snapshot.document.model.modelId,
      branchId: s.snapshot.branchId,
      baseRevision: s.snapshot.revision,
      baseCommitId: s.snapshot.commitId,
      operations: ops,
      summary:
        s.batches.length === 1
          ? s.batches[0].summary
          : `${s.batches.length} changes · ${s.batches[0].summary}`,
    };
    const retry = !!saveCommand.current;
    if (!retry) { savedBatches.current = [...s.batches]; savedDocument.current = s.draft; }
    const capturedBatches = savedBatches.current;
    saveCommand.current = command;
    try {
      let receipt: CommandReceipt | undefined;
      if (retry) {
        try {
          const lookup: any = await api(`/commands/${command.commandId}`);
          receipt = lookup.receipt || lookup;
        } catch (e: any) {
          if (e.status !== 404) throw e;
        }
      }
      if (!receipt)
        receipt = await api<CommandReceipt>(
          `/models/${command.modelId}/branches/${command.branchId}/commands`,
          { method: "POST", body: JSON.stringify(command) },
        );
      if (modelSaveRequiresReconciliation(receipt, savedDocument.current!) && canvasGesture.current) await new Promise<void>(resolve => gestureWaiters.current.push(resolve));
      const current = stateRef.current;
      const acknowledged = acknowledgeModelSave(receipt, capturedBatches, savedDocument.current!, {draft:current.draft!, batches:current.batches});
      const remaining = acknowledged.batches;
      const nextDraft = acknowledged.draft;
      stateRef.current = { ...current, snapshot: receipt.snapshot, draft: nextDraft, batches: remaining };
      setSnapshot(receipt.snapshot);
      if (nextDraft !== current.draft) setDraft(nextDraft);
      setBatches(remaining);
      setRemoteRevision(0);
      saveCommand.current = null;
      savedBatches.current = [];
      if (capturedBatches.length) localHistory.current.persisted = true;
      if (acknowledged.conflicts.length) {
        unresolvedAcknowledgement.current = true;
        setConflict({incoming:receipt.snapshot, conflicts:acknowledged.conflicts});
        setConflictOpen(false); setSaveFailure(true); setAutosaveBlocked(true);
        return false;
      }
      setSaveFailure(false);
      setAutosaveBlocked(false);
      refreshLists().catch(() => {});
      setRecentlySaved(true);
      clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setRecentlySaved(false), 2500);
      if (!automatic || stateRef.current.preferences.showAutosaveSuccess) notify(receipt.rebased ? "Saved after a compatible merge." : "All changes saved.", "success");
      return true;
    } catch (e: any) {
      setSaveFailure(true);
      setAutosaveBlocked(true);
      if (e.status === 409) {
        saveCommand.current = null;
        const incoming = e.details?.current || e.details?.snapshot;
        notify(
          "The model changed while you were editing. Your draft is preserved.",
          "error",
        );
        if (incoming?.document)
          { setConflict({ incoming, conflicts: e.details?.conflicts || [] }); setConflictOpen(false); }
        else setRemoteRevision(s.snapshot.revision + 1);
      } else if (e.status && e.status < 500) {
        saveCommand.current = null;
        notify(errorText(e), "error");
      } else {
        notify(
          "Save not confirmed. Your draft and command ID are retained. Retry checks the saved receipt first.",
          "error",
        );
      }
      return false;
    } finally {
      stateRef.current.saving = false;
      setSaving(false);
    }
    };
    const task = run();
    saveInFlight.current = task;
    void task.finally(() => { if (saveInFlight.current === task) saveInFlight.current = null; });
    return automatic ? task : task.then(ok => ok && (stateRef.current.batches.length || stateRef.current.inspectorDirty) ? save(false) : ok);
  };
  const refreshUndoState = async (modelId: string) => {
    try {
      const next = await api<UndoState>(`/models/${modelId}/undo-state`);
      if (stateRef.current.snapshot?.document.model.modelId === modelId) setUndoState(next);
    } catch {
      if (stateRef.current.snapshot?.document.model.modelId === modelId) setUndoState({ canUndo: false, canRedo: false });
    }
  };
  useEffect(() => {
    setUndoState({ canUndo: false, canRedo: false });
    if (snapshot) void refreshUndoState(snapshot.document.model.modelId);
  }, [snapshot?.document.model.modelId, snapshot?.revision]);
  const historyLocked = busy || navigationSaving || !!navigationGuard || !canChange || inspectorDirty || propertyDirty || !!dialog || !!appearanceTarget || !!propertyTarget || !!confirm;
  // Session operations retain their own history across persistence. Repository
  // history remains available when all unsaved local operations are undone.
  // Confirmed compensating saves retain local ownership, avoiding accidental Redo.
  const canUndo = !!draft && !historyLocked && !unresolvedAcknowledgement.current && (historyRequest.current?.action === "undo" || (!historyRequest.current && (localHistory.current.owned ? !!localHistory.current.past.length : !saving && !saveCommand.current && (!!batches.length || undoState.canUndo))));
  const canRedo = !!draft && !historyLocked && !unresolvedAcknowledgement.current && (historyRequest.current?.action === "redo" || (!historyRequest.current && (serverUndoDepth === 0 && !!localHistory.current.future.length || (!localHistory.current.owned && !saving && !saveCommand.current && ((serverUndoDepth > 0 && undoState.canRedo && !repositoryRedoInvalidated) || (serverUndoDepth === 0 && (!!redoBatches.length || (!batches.length && undoState.canRedo && !repositoryRedoInvalidated))))))));
  const runRepositoryHistory = async (action: "undo" | "redo") => {
    if (!snapshot) return;
    const request = historyRequest.current || { action, requestId: uid(), modelId: snapshot.document.model.modelId, baseRevision: snapshot.revision };
    if (request.action !== action) return;
    historyRequest.current = request;
    setSaving(true);
    try {
      const receipt: any = await api(`/models/${request.modelId}/${action}`, {
        method: "POST", body: JSON.stringify({ requestId: request.requestId, baseRevision: request.baseRevision, branchId: snapshot.branchId }),
      });
      const accepted = receipt.snapshot || receipt.receipt?.snapshot;
      if (!accepted?.document) throw new Error("The command response did not include the saved model.");
      setSnapshot(accepted);
      setDraft(accepted.document);
      setRemoteRevision(0);
      setInspectorGeneration((value) => value + 1);
      setServerUndoDepth((depth) => action === "undo" ? depth + 1 : Math.max(0, depth - 1));
      setRepositoryRedoInvalidated(false);
      historyRequest.current = null;
      notify(action === "undo" ? "Your last accepted command was undone." : "Your undone command was restored.", "success");
      void refreshLists().catch(() => {});
      void refreshUndoState(request.modelId);
    } catch (e: any) {
      if (e.status && e.status < 500) {
        historyRequest.current = null;
        notify(errorText(e), "error");
        void refreshUndoState(request.modelId);
      } else {
        notify(`${action === "undo" ? "Undo" : "Redo"} is not confirmed. Retry the same action to check its command receipt.`, "error");
      }
    } finally {
      setSaving(false);
    }
  };
  const applyLocalHistory = (direction: "undo" | "redo") => {
    const history = localHistory.current;
    const source = direction === "undo" ? history.past : history.future;
    const entry = source.at(-1);
    if (!entry || !stateRef.current.draft) return false;
    const operations = direction === "undo" ? entry.inverse : entry.operations;
    if (!operations?.length) return false;
    try {
      const current = stateRef.current;
      const result = applyOperations(current.draft!, operations);
      const reversiblePending = direction === "undo" && current.batches.at(-1) === entry && !savedBatches.current.includes(entry);
      const restored = {...entry, inverse:result.inverse};
      const next = reversiblePending ? current.batches.slice(0, -1) : [...current.batches, direction === "redo" ? restored : {operations, inverse:result.inverse, summary:`Undo: ${entry.summary}`}];
      const releaseRepositoryHistory = direction === "undo" && history.past.length === 1 && !history.persisted && !next.length && !saveCommand.current;
      localHistory.current = direction === "undo"
        ? {...history, past: history.past.slice(0, -1), future:[...history.future, entry], owned:!releaseRepositoryHistory}
        : {...history, past:[...history.past, restored], future:history.future.slice(0, -1), owned:true};
      stateRef.current = {...current, draft:result.document, batches:next};
      setDraft(result.document); setBatches(next); setAutosaveBlocked(false);
      return true;
    } catch (error) { notify(errorText(error), "error"); return true; }
  };
  const undo = async () => {
    if (!canUndo) return;
    if (applyLocalHistory("undo")) return;
    if (batches.length && !historyRequest.current) {
      const removed = batches[batches.length - 1];
      const next = batches.slice(0, -1);
      try {
        let document = snapshot!.document;
        for (const batch of next) document = applyOperations(document, batch.operations).document;
        setBatches(next);
        setRedoBatches((previous) => [...previous, removed]);
        setDraft(document);
        setAutosaveBlocked(false);
        notify(`Undone: ${removed.summary}`);
      } catch (e) { notify(errorText(e), "error"); }
      return;
    }
    await runRepositoryHistory("undo");
  };
  const redo = async () => {
    if (!canRedo) return;
    if (serverUndoDepth === 0 && applyLocalHistory("redo")) return;
    if (redoBatches.length && serverUndoDepth === 0 && !historyRequest.current) {
      const restored = redoBatches[redoBatches.length - 1];
      try {
        const next = applyOperations(draft!, restored.operations).document;
        setDraft(next);
        setBatches((previous) => [...previous, restored]);
        setRedoBatches((previous) => previous.slice(0, -1));
        setAutosaveBlocked(false);
        notify(`Restored: ${restored.summary}`);
      } catch (e) {
        notify(`Redo could not be applied to the current draft. ${errorText(e)}`, "error");
      }
      return;
    }
    await runRepositoryHistory("redo");
  };
  const autosaveState = useRef({eligible:false, save: () => Promise.resolve(false)});
  autosaveState.current = {
    eligible: preferences.saveMode === "automatic" && !!(pendingCount || inspectorDirty || propertyDirty) && !navigationGuard && !navigationSaving && !saving && !autosaveBlocked && !canvasGestureActive && !composing.current,
    save: () => save(true),
  };
  useEffect(() => {
    if (preferences.autosaveIntervalSeconds > 0 || !autosaveState.current.eligible) return;
    const timer = setTimeout(() => { if (autosaveState.current.eligible && !composing.current) void autosaveState.current.save(); }, inspectorDirty || propertyDirty ? 600 : 120);
    return () => clearTimeout(timer);
  }, [pendingCount, preferences.saveMode, preferences.autosaveIntervalSeconds, saving, autosaveBlocked, inspectorDirty, propertyDirty, formEditRevision, navigationGuard, navigationSaving, canvasGestureActive]);
  useEffect(() => {
    if (preferences.saveMode !== "automatic" || !(preferences.autosaveIntervalSeconds > 0)) return;
    // This clock deliberately does not depend on keystrokes or pending counts.
    const timer = setInterval(() => { if (autosaveState.current.eligible && !composing.current) void autosaveState.current.save(); }, preferences.autosaveIntervalSeconds * 1000);
    return () => clearInterval(timer);
  }, [preferences.saveMode, preferences.autosaveIntervalSeconds, snapshot?.document.model.modelId]);
  const receive = async (force = false) => {
    const initial = stateRef.current;
    if (!initial.snapshot || initial.saving || historyRequest.current || saveCommand.current || refreshInFlight.current || modelLoadInFlight.current) return;
    const navigationGeneration = modelOpenRequest.current, baselineRevision = initial.snapshot.revision;
    const modelId = initial.snapshot.document.model.modelId, branchId = initial.snapshot.branchId;
    refreshInFlight.current = true; setRefreshing(true);
    try {
      const incoming = await api<ModelSnapshot>(`/models/${modelId}/branches/${branchId}`);
      // Compare against the current draft after the request; edits made while waiting must survive.
      const s = stateRef.current;
      if (modelOpenRequest.current !== navigationGeneration || modelLoadInFlight.current || s.snapshot?.document.model.modelId !== modelId || s.snapshot.branchId !== branchId || s.snapshot.revision !== baselineRevision || s.saving || saveCommand.current || historyRequest.current) return;
      if (incoming.revision <= s.snapshot.revision) { if(force) notify("This model is already up to date.", "success"); return; }
      if (s.batches.length || s.inspectorDirty) {
        const ops = s.batches.flatMap(b => b.operations);
        const conflicts = operationConflicts(s.snapshot.document, incoming.document, ops);
        if (!conflicts.length && !s.inspectorDirty) {
          const rebased = applyOperations(incoming.document, ops).document;
          stateRef.current = { ...s, snapshot: incoming, draft: rebased };
          setSnapshot(incoming); setDraft(rebased); setRedoBatches([]); setServerUndoDepth(0);
          setRemoteRevision(0); setConflict(null);
          notify("Received compatible changes; your unsaved edits are preserved.");
        } else {
          setConflict({ incoming, conflicts }); setConflictOpen(true); setRemoteRevision(incoming.revision);
          notify("Incoming changes need review. Your local edits are retained.");
        }
      } else {
        acceptSnapshot(incoming); setConflict(null);
        notify("Received the latest saved revision.", "success");
      }
    } catch (e) { notify(errorText(e), "error"); }
    finally { refreshInFlight.current = false; setRefreshing(false); }
  };
  useEffect(() => {
    const timer = setInterval(async () => {
      const s = stateRef.current;
      if (!s.snapshot || s.saving || saveCommand.current || historyRequest.current || busyPoll.current) return;
      busyPoll.current = true;
      try {
        const r: any = await api(
          `/models/${s.snapshot.document.model.modelId}/events?since=${s.snapshot.revision}&branchId=${encodeURIComponent(s.snapshot.branchId)}`,
        );
        if(stateRef.current.snapshot?.document.model.modelId!==s.snapshot.document.model.modelId)return;
        setConnection("online");
        if (r.revision > s.snapshot.revision) {
          setRemoteRevision(r.revision);
          if (s.preferences.receiveMode === "automatic" && !s.inspectorDirty)
            await receive();
        }
      } catch {
        if(stateRef.current.snapshot?.document.model.modelId===s.snapshot.document.model.modelId)setConnection("retrying");
      } finally {
        busyPoll.current = false;
      }
    }, 2000);
    return () => clearInterval(timer);
  }, []);
  const runCanvas = (type: string) =>
    setCanvasCommand({ id: Date.now(), type });
  const openHelp = (topic = "getting-started") => {
    if (propertyTarget || appearanceTarget || dialog) { setHelpOverlayTopic(topic); return; }
    setHelpTopic(topic);
    setRightPanel("help");
    setDockOpen(true);
    setFocus(false);
  };
  const deleteObject = (id: string) => {
    const o = draft?.objects.find((o) => o.objectId === id);
    const r = draft?.relationships.find((r) => r.relationshipId === id);
    setConfirm({
      title: `Delete ${o?.name || r?.name || "selection"}?`,
      message: o
        ? "This removes the object and its dependent fields and relationships from this draft. Review the result before saving."
        : "This removes the relationship from the draft. The endpoint entities are retained.",
      label: "Delete from draft",
      action: () => {
        apply(
          [
            {
              type: o ? "object.delete" : "relationship.delete",
              ...(o ? { objectId: id, cascade: true } : { relationshipId: id }),
            },
          ],
          `Delete ${o?.name || r?.name}`,
        );
        setSelection([]);
      },
    });
  };
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      const typing =
        ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName) ||
        el.isContentEditable;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        void save();
      }
      if ((e.ctrlKey || e.metaKey) && !typing && ["z", "y"].includes(e.key.toLowerCase())) {
        e.preventDefault();
        if (e.key.toLowerCase() === "y" || e.shiftKey) void redo();
        else void undo();
      }
      if (e.key === "Delete" && !typing && !propertyTarget && !viewRequest && !dialog && !appearanceTarget && !confirm && !navigationGuard && !navigationSaving && !busy && !inspectorDirty && selection[0] && canEdit)
        deleteObject(selection[0]);
      if (e.key === "Escape") {
        setContext(null);
        if (focus && !dialog && !propertyTarget && !appearanceTarget && !confirm && !navigationGuard && !navigationSaving && !helpOverlayTopic) setFocus(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selection, batches, redoBatches, undoState, serverUndoDepth, snapshot, saving, focus, canEdit, canUndo, canRedo, propertyTarget, appearanceTarget, viewRequest, dialog, confirm, navigationGuard, navigationSaving, busy, inspectorDirty, helpOverlayTopic]);
  const createModel = async (data: any) => {
    const result: any = await api("/models", {
      method: "POST",
      body: JSON.stringify(data),
    });
    const s = result.snapshot || result;
    await refreshLists();
    acceptSnapshot(s);
    setTabs((prev) => [
      ...prev.filter((t) => t.modelId !== s.document.model.modelId),
      {
        ...s.document.model,
        modelId: s.document.model.modelId,
        branchId: s.branchId,
        repositoryId: s.repositoryId,
        revision: s.revision,
        updatedAt: s.updatedAt,
        objectCount: s.document.objects.length,
      },
    ]);
    setRepositoryId(s.repositoryId);
    setDiagramId(s.document.diagrams.find((diagram: Diagram) => diagram.representationId === s.document.model.activeRepresentation)?.diagramId || s.document.diagrams[0]?.diagramId || "");
    setSearch("");
    setPreferences(previous => ({...previous, explorerSections:{...previous.explorerSections,models:true,diagrams:true}}));
    requestAnimationFrame(() => globalThis.document.querySelector(`[data-model-id="${s.document.model.modelId}"]`)?.scrollIntoView({block:"nearest"}));
  };
  const diagnostics = useMemo(
    () => (draft ? validateModel(draft) : []),
    [draft],
  );
  const errors = diagnostics.filter((d) => d.severity === "error").length;
  const warnings = diagnostics.filter((d) => d.severity === "warning").length;
  const activeDiagram =
    draft?.diagrams.find((d) => d.diagramId === diagramId) ||
    draft?.diagrams[0];
  const terms = draft ? vocabulary(draft, activeDiagram?.representationId, undefined, activeDiagram?.perspective) : { object: "entity", objects: "entities", field: "attribute", fields: "attributes", conceptual: false, physical: false };
  const activeRepo = repositories.find(
    (r) => r.repositoryId === repositoryId,
  );
  const roots =
    draft?.objects.filter(
      (o) =>
        !o.ownerObjectId &&
        (!activeDiagram ||
          o.representationId === activeDiagram.representationId),
    ) || [];
  const visibleModels = models.filter(
    (m) =>
      (!repositoryId || m.repositoryId === repositoryId) &&
      m.name.toLowerCase().includes(search.toLowerCase()),
  );
  const activeTab = tabs.find((t) => t.modelId === draft?.model.modelId);
  const isFocus = focus || preferences.workspace === "focus";
  const preferenceRequest = useRef(0);
  const preferenceQueue = useRef<Promise<void> | null>(null);
  const pendingPreferences = useRef<Partial<Preferences>>({});
  const preferenceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const preferenceSavedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const preferenceGesture = useRef(false);
  const flushPreferences = (): Promise<void> => {
    clearTimeout(preferenceTimer.current);
    if (preferenceQueue.current) return preferenceQueue.current.then(() => flushPreferences());
    const patch = pendingPreferences.current;
    if (!Object.keys(patch).length || preferenceGesture.current) return Promise.resolve();
    pendingPreferences.current = {};
    const request = preferenceRequest.current;
    setPreferenceSaveState("saving");
    clearTimeout(preferenceSavedTimer.current);
    const task = (async () => {
      try {
        const saved:any = await api("/preferences", {method:"PATCH", body:JSON.stringify(patch)});
        if (request === preferenceRequest.current) {
          const next = {...defaultPreferences, ...(saved.preferences || saved)};
          stateRef.current.preferences = next; setPreferences(next);
        }
        setPreferenceSaveError("");
        setPreferenceSaveState(Object.keys(pendingPreferences.current).length ? "pending" : "saved");
        preferenceSavedTimer.current = setTimeout(() => setPreferenceSaveState(state => state === "saved" ? "idle" : state), 2500);
      } catch (error) {
        // A newer local value always wins over the failed request's value.
        pendingPreferences.current = {...patch, ...pendingPreferences.current};
        setPreferenceSaveError("Preferences could not be saved. " + errorText(error));
        setPreferenceSaveState("error");
        throw error;
      } finally { preferenceQueue.current = null; }
    })();
    preferenceQueue.current = task;
    return task;
  };
  const displayPreferenceKeys = new Set(["attributeDisplayLimit","showAllAttributes","fontSize","titleSize","fontFamily","autoSizeEntities","resizeOnDetailChange","detailLevel","diagramDetailLevels","entityDisplay","diagramEntityDisplay","showEntityDescription","modelTypePresentation"]);
  const preferenceGeometry = (next:Preferences, patch:Partial<Preferences>) => {
    if (!stateRef.current.draft || !canDiagramEdit || !Object.keys(patch).some(key=>displayPreferenceKeys.has(key))) return [];
    return prepareDiagramPreferenceOperations(stateRef.current.draft,stateRef.current.preferences,next);
  };
  const updatePreferences = (patch: Partial<Preferences>, debounce=true) => {
    preferenceRequest.current++;
    const next={...stateRef.current.preferences,...patch};
    try {
      const geometry=preferenceGeometry(next,patch);
      if (geometry.length && !apply(geometry,"Adjust spacing for displayed details",next)) return;
    } catch (error) { notify(errorText(error),"error");return; }
    stateRef.current.preferences=next;setPreferences(next);
    pendingPreferences.current={...pendingPreferences.current,...patch};
    clearTimeout(preferenceTimer.current);
    setPreferenceSaveState("pending");
    if (!preferenceGesture.current) {
      if (debounce) preferenceTimer.current = setTimeout(() => void flushPreferences().catch(() => {}), 450);
      else void flushPreferences().catch(() => {});
    }
  };
  scaleChange.current = value => { const next=normalizeInterfaceScale(value);scaleRef.current=next;setInterfaceScale(next);updatePreferences({interfaceScale:next}); };
  const flushRef=useRef(flushPreferences);flushRef.current=flushPreferences;
  useEffect(()=>{
    const leave=()=>void flushRef.current().catch(()=>{});
    const begin = (event: PointerEvent) => {
      if (event.target instanceof HTMLInputElement && event.target.type === "range") { preferenceGesture.current = true; clearTimeout(preferenceTimer.current); }
    };
    const end = () => { if (preferenceGesture.current) { preferenceGesture.current = false; leave(); } };
    document.addEventListener("pointerdown", begin, true); document.addEventListener("pointerup", end); document.addEventListener("pointercancel", end);
    window.addEventListener("pagehide",leave);
    return()=>{window.removeEventListener("pagehide",leave);document.removeEventListener("pointerdown",begin,true);document.removeEventListener("pointerup",end);document.removeEventListener("pointercancel",end);clearTimeout(preferenceTimer.current);clearTimeout(preferenceSavedTimer.current);};
  },[]);
  const guardProperties = (action: () => void) => {
    if (inspectorDirty || propertyDirty) {
      setConfirm({
        title: "Unapplied property edits",
        message: "Apply your property edits first, or discard them to continue. Your staged model changes are retained.",
        label: "Discard property edits",
        action: () => {
          setInspectorDirty(false);
          setPropertyDirty(false);
          setInspectorGeneration((value) => value + 1);
          action();
        },
      });
    } else action();
  };
  const selectObjects = (ids: string[]) => {
    const select = () => {
      setSelection(ids);
      if (preferences.showAttributePropertiesOnSelection !== false && ids.some(id => draft?.objects.some(o => o.objectId === id && !!o.ownerObjectId))) {
        setRightPanel("inspector"); setDockOpen(true); setFocus(false);
        if (preferences.workspace === "focus") updatePreferences({workspace:"balanced"});
      }
    };
    if (selection.join() === ids.join()) { select(); return; }
    guardProperties(select);
  };
  const openProperties = (target: AppearanceTarget) => guardProperties(() => {
    setContext(null);
    if (target.kind === "entity" || target.kind === "relationship") {
      if (target.id) setSelection([target.id]);
      if (preferences.propertyPresentation === "inspector") {
        setRightPanel("inspector"); setDockOpen(true); setFocus(false); setPropertyTarget(null); return;
      }
    }
    setPropertyTarget(target);
    setPropertyDirty(false);
  });
  const openAppearance = (target: AppearanceTarget) => {
    setContext(null);
    const object = target.kind === "entity" && draft?.objects.find((item) => item.objectId === target.id);
    setAppearanceTarget(object && object.ownerObjectId ? { kind: "entity", id: object.ownerObjectId } : target);
  };
  const showNavigatorContext = (e: React.MouseEvent, target: {kind: NonNullable<typeof context>["kind"]; id?: string}) => {
    e.preventDefault(); e.stopPropagation();
    setContext({ x: Math.min(e.clientX, window.innerWidth - 238), y: Math.min(e.clientY, window.innerHeight - 290), kind: target.kind, id: target.id || draft?.model.modelId || "" });
  };
  const requestView = (mode:"subject-area"|"view"|"add", sourceId=activeDiagram?.diagramId, selected=selection) => {
    if (!sourceId || !draft) return;
    const source=draft.diagrams.find(d=>d.diagramId===sourceId);
    if (!source) return;
    setContext(null);
    guardProperties(()=>setViewRequest({mode,sourceDiagramId:sourceId,selection:selected,
      ...(mode==="add"?{targetDiagramId:sourceId}:{}),
      ...(mode==="view"?{parentDiagramId:source.role==="subject-area"?sourceId:source.parentDiagramId}:{})}));
  };
  const openModule = (id:string) => {
    setContext(null);
    if (id==="settings") {setDialog("preferences");return;}
    if (id==="admin") {if(canAdmin)setDialog("admin");return;}
    if (id==="blocks") {if(canEdit&&draft)setDialog("blocks");return;}
    if (id==="help") {openHelp();return;}
    setRightPanel(id==="rail"?"inspector":id);setDockOpen(true);setFocus(false);
    if(preferences.workspace==="focus")updatePreferences({workspace:"balanced"});
  };
  const contextDiagram = draft?.diagrams.find(d=>d.diagramId===(context?.kind==="diagram"?context.id:activeDiagram?.diagramId));
  const cancelRelationship = () => { setRelationshipPicking(false); setRelationshipSource(null); };
  const beginRelationship = (sourceId?: string) => guardProperties(() => {
    if (!canEdit || !!historyRequest.current || !activeDiagram) return;
    setContext(null);
    if (preferences.relationshipCreationMode === "dialog") {
      cancelRelationship(); setAddRelationship({ sourceId });
    } else {
      setRelationshipSource(sourceId || null); setRelationshipPicking(true);
    }
  });
  const pickRelationshipEntity = (targetId: string) => {
    if (!relationshipPicking || !draft || !activeDiagram) return;
    if (!draft.objects.some(o => o.objectId === targetId && !o.ownerObjectId && o.representationId === activeDiagram.representationId)) {
      notify("Choose an entity in the active representation.", "error"); return;
    }
    if (!relationshipSource) setRelationshipSource(targetId);
    else { setAddRelationship({ sourceId: relationshipSource, targetId }); cancelRelationship(); }
  };
  useEffect(cancelRelationship, [draft?.model.modelId, activeDiagram?.diagramId]);
  useEffect(() => { const cancel = (e: KeyboardEvent) => { if(e.key === "Escape") cancelRelationship(); }; window.addEventListener("keydown",cancel); return () => window.removeEventListener("keydown",cancel); }, []);
  type ExplorerSection = "models" | "diagrams" | "objects" | "relationships";
  const sectionExpanded = (section: ExplorerSection) => section === "relationships" ? preferences.explorerSections?.relationships === true : preferences.explorerSections?.[section] !== false;
  const toggleSection = (section: ExplorerSection) => updatePreferences({ explorerSections: { ...preferences.explorerSections, [section]: !sectionExpanded(section), ...(section === "relationships" ? {relationshipsChoice: sectionExpanded(section) ? "collapsed" as const : "expanded" as const} : {}) } });
  const sectionHeading = (section: ExplorerSection, title: string, count: number) => <div className="tree-section-heading" onContextMenu={e => showNavigatorContext(e, {kind: section})}><button className="tree-section-toggle" aria-label={`${sectionExpanded(section) ? "Collapse" : "Expand"} ${title}`} aria-expanded={sectionExpanded(section)} aria-controls={`explorer-${section}`} onClick={() => toggleSection(section)}>{sectionExpanded(section) ? <Minus size="1.2em"/> : <Plus size="1.2em"/>}</button><span title={title}>{title.toUpperCase()}</span><span>{count}</span></div>;
  const canAddField = (ownerId: string) => canEdit || (developer && draft?.objects.find((object) => object.objectId === ownerId)?.kind === "table");
  const selectionReadOnly = (ids: string[]) => readOnly || busy || !!historyRequest.current || !!draft?.representations.some((representation) => representation.baseline && representation.representationId === (draft.objects.find((o) => o.objectId === ids[0])?.representationId || draft.relationships.find((r) => r.relationshipId === ids[0])?.representationId));
  const relationshipCollisionResolution = useMemo(() => {
    if(!conflict||!draft||!snapshot||!conflict.conflicts.length||conflict.conflicts.some(item=>!/^relationship\..+\.name$/.test(item.path)))return null;
    const replacements=new Map<string,string>();
    const pendingIds=new Set(conflict.conflicts.map(item=>item.objectId));
    const suggestionDocument={...draft,relationships:[...conflict.incoming.document.relationships,...draft.relationships.filter(r=>!pendingIds.has(r.relationshipId)&&!conflict.incoming.document.relationships.some(existing=>existing.relationshipId===r.relationshipId))]};
    const proposals:{id:string;before:string;after:string}[]=[];
    for(const item of conflict.conflicts){
      const relation=draft.relationships.find(r=>r.relationshipId===item.objectId);
      if(!relation)return null;
      const proposed=relationshipSuggestion(suggestionDocument,relation.representationId,relation.sourceObjectId,relation.targetObjectId).name;
      if(!proposed)return null;
      replacements.set(relation.relationshipId,proposed);proposals.push({id:relation.relationshipId,before:relation.name,after:proposed});
      suggestionDocument.relationships.push({...relation,name:proposed});
    }
    const nextBatches:Batch[]=batches.map(batch=>({...batch,operations:batch.operations.map((op):Operation=>{
      if(op.type==="relationship.create"&&op.relationship&&replacements.has(op.relationship.relationshipId))return {...op,relationship:{...op.relationship,name:replacements.get(op.relationship.relationshipId)!}};
      if(op.type==="relationship.update"&&op.relationshipId&&replacements.has(op.relationshipId)&&Object.hasOwn(op.changes||{},"name"))return {...op,changes:{...op.changes,name:replacements.get(op.relationshipId)!}};
      return op;
    })}));
    try {
      const operations=nextBatches.flatMap(batch=>batch.operations);
      if(operationConflicts(snapshot.document,conflict.incoming.document,operations).length)return null;
      const document=applyOperations(conflict.incoming.document,operations).document;
      return {proposals,batches:nextBatches,document};
    } catch {return null;}
  },[conflict,draft,snapshot,batches]);
  const applyRelationshipCollisionResolution=()=>{
    if(!conflict||!relationshipCollisionResolution)return;
    setSnapshot(conflict.incoming);setDraft(relationshipCollisionResolution.document);setBatches(relationshipCollisionResolution.batches);setRedoBatches([]);setServerUndoDepth(0);setRepositoryRedoInvalidated(true);setRemoteRevision(0);setConflict(null);unresolvedAcknowledgement.current=false;setSaveFailure(false);setAutosaveBlocked(false);saveCommand.current=null;
    notify("Accepted the proposed relationship names. Your other draft edits are retained; save to commit.");
  };
  const closeTab = (id: string) => {
    const action = () => {
      setTabs((prev) => prev.filter((t) => t.modelId !== id));
      if (draft?.model.modelId === id) {
        setSnapshot(null);
        setDraft(null);
        setBatches([]);
        setSelection([]);
      }
    };
    if (id === draft?.model.modelId) navigate(action);
    else action();
  };
  return (
    <WorkspaceToolsProvider preferences={preferences} onChange={(patch) => void updatePreferences(patch)}>
    <div
      className={`app-shell density-${preferences.density} workspace-${preferences.workspace} ${isFocus ? "canvas-focus" : ""}`}
      onClick={() => context && setContext(null)}
    >
      <WorkspaceHeader className="app-header">
        <Brand name={session.branding?.name || "DataGlow"} />
        <span className="product-edition">ER DESIGNER</span>
        <div className="header-divider" />
        <div className="header-breadcrumb">
          <span>{session.branding?.name || session.organizationCode}</span>
          <ChevronRight size={12} />
          <strong>{draft?.model.name || "Workspace"}</strong>
          {draft && (
            <span className="header-type">
              {modelTypeLabel(draft.model.modelType)}
            </span>
          )}
        </div>
        <div className="header-spacer" />
        {preferences.workspaceLayout?.toolbars.models?.visible===false&&<select className="header-model-switch" aria-label="Switch open model" value={draft?.model.modelId||""} onChange={e=>{const id=e.target.value;navigate(()=>{if(id)void openModel(id);else{setSnapshot(null);setDraft(null);setBatches([]);}});}}><option value="">Overview</option>{tabs.map(tab=><option key={tab.modelId} value={tab.modelId}>{tab.name}</option>)}</select>}
        <label className="interface-scale-control" title="Interface size is saved to your preferences. Scroll over this header to resize controls; diagram zoom stays unchanged."><span>UI</span><input type="range" min="0.5" max="2" step="0.1" value={interfaceScale} aria-label="Interface size" onChange={event => scaleChange.current(Number(event.target.value))}/><output>{Math.round(interfaceScale * 100)}%</output></label>
        <PaletteVisibilityControls />
        <WorkspaceMenuButton />
        <CustomizePalettesButton />
        <span className="alpha-badge">{PRODUCT_RELEASE.toUpperCase()}</span>
        <IconButton
          label="Workspace preferences"
          icon={Settings2}
          disabled={!preferencesReady}
          onClick={() => setDialog("preferences")}
        />
        {preferenceSaveError ? <button className="preference-save-error" title={preferenceSaveError} aria-label="Retry saving preferences" onClick={()=>void flushPreferences().catch(()=>{})}>Preferences not saved · Retry</button> : preferenceSaveState === "saving" || preferenceSaveState === "saved" ? <span role="status" className="preference-save-state">Preferences {preferenceSaveState === "saving" ? "Saving…" : "Saved"}</span> : null}
        <button
          className="user-menu"
          title={`${session.name} · ${session.roles.join(", ")}`}
          onClick={() =>
            setConfirm({
              title: "Sign out of this studio?",
              message:
                pendingCount || inspectorDirty || propertyDirty
                  ? "You have unsaved local changes. Save or export them before signing out, or confirm to discard them."
                  : "Your saved models remain in the studio.",
              label: "Sign out",
              action: async () => {
                try {
                  await flushPreferences();
                  await api("/auth/logout", { method: "POST" });
                  onLogout();
                } catch (e) {
                  notify(errorText(e), "error");
                }
              },
            })
          }
        >
          <span className="avatar">
            {session.name
              .split(" ")
              .map((w) => w[0])
              .slice(0, 2)
              .join("")
              .toUpperCase()}
          </span>
          <span>{session.name.split(" ")[0]}</span>
          <ChevronDown size={12} />
        </button>
      </WorkspaceHeader>
      <DockSlot zone="header" />
      <DockableToolbar id="commands" className="command-bar">
        <div className="primary-actions">
          <WorkspaceCommand id="new-model"><Button
            icon={Plus}
            variant="primary"
            disabled={!canCreate}
            title={!canCreate ? "Requires model.create permission" : undefined}
            onClick={() => navigate(() => setDialog("new-model"))}
          >
            New model
          </Button></WorkspaceCommand>
          <WorkspaceCommand id="save"><Button
            icon={Save}
            disabled={(!pendingCount && !inspectorDirty && !propertyDirty && !saveCommand.current) || busy || saving || !canChange || !!historyRequest.current}
            busy={saving}
            onClick={() => void save()}
          >
            Save
          </Button></WorkspaceCommand>
          <WorkspaceCommand id="autosave"><label className="autosave-toggle" title="Automatically persist completed edits in the background">
            <input type="checkbox" role="switch" aria-label="AutoSave" checked={preferences.saveMode === "automatic"} disabled={!preferencesReady} onChange={event => updatePreferences({saveMode: event.target.checked ? "automatic" : "manual"})}/><span>AutoSave</span><small>{preferences.saveMode === "automatic" ? <strong>On</strong> : "Off"}</small>
          </label></WorkspaceCommand>
          <WorkspaceCommand id="refresh"><IconButton label="Refresh saved model" icon={RefreshCw} disabled={!draft || saving || refreshing || !!saveCommand.current || !!historyRequest.current} className={refreshing ? "refreshing" : ""} title="Receive teammates' latest saved changes; retain pending local edits" onClick={() => void receive(true)}/></WorkspaceCommand>
          <div className="history-controls" role="group" aria-label="Undo and redo">
            <WorkspaceCommand id="undo"><Button icon={Undo2} disabled={!canUndo} title={inspectorDirty || propertyDirty ? "Apply or discard property edits first" : `Undo · Ctrl+Z${batches.length ? ` · ${batches[batches.length - 1].summary}` : undoState.undoSummary ? ` · ${undoState.undoSummary}` : ""}`} onClick={() => void undo()}>Undo</Button></WorkspaceCommand>
            <WorkspaceCommand id="redo"><Button icon={Redo2} disabled={!canRedo} title={`Redo · Ctrl+Shift+Z / Ctrl+Y${redoBatches.length ? ` · ${redoBatches[redoBatches.length - 1].summary}` : undoState.redoSummary ? ` · ${undoState.redoSummary}` : ""}`} onClick={() => void redo()}>Redo</Button></WorkspaceCommand>
          </div>
          <div className="toolbar-divider" />
          <WorkspaceCommand id="history"><Button
            icon={History}
            disabled={!draft}
            onClick={() => setDialog("history")}
          >
            History
          </Button></WorkspaceCommand>
          <WorkspaceCommand id="import"><Button
            icon={ArrowUpFromLine}
            disabled={!canCreate}
            title={!canCreate ? "Requires model.create permission" : undefined}
            onClick={() => navigate(() => setDialog("import"))}
          >
            Import
          </Button></WorkspaceCommand>
          <WorkspaceCommand id="export-model"><Button
            icon={ArrowDownToLine}
            disabled={!draft || !canExport}
            title={!canExport ? "Requires model.export permission" : undefined}
            onClick={() => setDialog("export")}
          >
            Export
          </Button></WorkspaceCommand>
          {terms.physical && <WorkspaceCommand id="database"><Button
            icon={Database}
            disabled={!draft}
            onClick={() => setDialog("database")}
          >
            Database
          </Button></WorkspaceCommand>}
        </div>
        <div className="save-state" role="status" aria-live="polite">
          <span
            className={`status-dot ${pendingCount || inspectorDirty || propertyDirty || saveFailure ? "amber" : ""}`}
          />
          {saving
            ? "Saving…"
            : saveCommand.current
              ? "Save unconfirmed"
              : historyRequest.current
                ? `${historyRequest.current.action === "undo" ? "Undo" : "Redo"} unconfirmed · retry action`
                : propertyDirty || inspectorDirty
                  ? "Unapplied property edits"
                  : saveFailure
                    ? "Save failed · edits retained"
                    : conflict || remoteRevision > (snapshot?.revision ?? 0)
                      ? "Incoming changes available"
                      : pendingCount
                ? `${autosaveBlocked && preferences.saveMode === "automatic" ? "Autosave paused · " : ""}${pendingCount} unsaved operations`
                : draft
                  ? recentlySaved ? "Saved" : "Up to date"
                  : "Ready to model"}
          {conflict && <button className="save-conflict-review" onClick={() => setConflictOpen(true)}>Review save conflict</button>}
          {draft && (
            <span className="save-mode">
              {preferences.saveMode === "automatic" ? "Auto" : "Manual"}
            </span>
          )}
        </div>
      </DockableToolbar>
      <DockSlot zone="commands" />
      <DockSlot zone="header-secondary" />
      <div className="workspace-body">
        <nav
          className={`mode-rail ${!preferences.railLabels ? "icons-only" : ""}`}
          onContextMenu={e=>showNavigatorContext(e,{kind:"module",id:"rail"})}
          aria-label="Workspace modes"
        >
          {[
            { id: "inspector", label: "Design", icon: Network },
            { id: "perspective", label: "Views", icon: LayersIcon },
            { id: "blocks", label: "Blocks", icon: Boxes },
            { id: "assistant", label: "Assistant", icon: Sparkles },
          ].map((item) => (
            <button
              key={item.id}
              onContextMenu={e=>showNavigatorContext(e,{kind:"module",id:item.id})}
              className={rightPanel === item.id ? "active" : ""}
              title={item.label}
              onClick={() => {
                if (!draft && item.id !== "inspector") {
                  notify(
                    "Open a model to use " + item.label.toLowerCase() + ".",
                  );
                  return;
                }
                if (item.id === "blocks" && !canEdit) {
                  notify(
                    "Inserting blocks requires model.edit permission.",
                    "error",
                  );
                  return;
                }
                if (item.id === "blocks") {
                  if (draft) setDialog("blocks");
                  else
                    notify("Open a model before inserting a building block.");
                } else {
                  setRightPanel(item.id);
                  setDockOpen(true);
                  setFocus(false);
                }
              }}
            >
              <item.icon size={19} />
              <span>{item.label}</span>
            </button>
          ))}
          <div className="rail-fill" />
          {canAdmin && (
            <button title="Administration" onContextMenu={e=>showNavigatorContext(e,{kind:"module",id:"admin"})} onClick={() => setDialog("admin")}>
              <Shield size={19} />
              <span>Admin</span>
            </button>
          )}
          <button
            className={rightPanel === "help" ? "active" : ""}
            title="Help"
            onContextMenu={e=>showNavigatorContext(e,{kind:"module",id:"help"})}
            onClick={() => openHelp()}
          >
            <CircleHelp size={20} />
            <span>Help</span>
          </button>
          <button
            title={
              preferencesReady ? "Preferences" : "Loading personal preferences"
            }
            onContextMenu={e=>showNavigatorContext(e,{kind:"module",id:"settings"})}
            disabled={!preferencesReady}
            onClick={() => setDialog("preferences")}
          >
            <SlidersHorizontal size={19} />
            <span>Settings</span>
          </button>
        </nav>
        {!isFocus && navOpen && (
          <aside className="navigator" style={{width:clampPanelWidth(explorerWidth ?? preferences.workspaceLayout.explorerWidth ?? 225*interfaceScale,viewportWidth)}} onContextMenu={e => showNavigatorContext(e, {kind:"explorer"})}>
            <PanelResizeHandle side="explorer" width={clampPanelWidth(explorerWidth ?? preferences.workspaceLayout.explorerWidth ?? 225*interfaceScale,viewportWidth)} onResize={setExplorerWidth} onCommit={width=>{updatePreferences({workspaceLayout:{...preferences.workspaceLayout,explorerWidth:width}});setExplorerWidth(null);}}/>
            <div className="panel-heading">
              <span>EXPLORER</span>
              <IconButton
                label="Collapse explorer"
                icon={PanelLeftClose}
                onClick={() => setNavOpen(false)}
              />
            </div>
            <div className="repository-picker">
              <Folder size={16} />
              <select
                value={repositoryId}
                onChange={(e) => { const id=e.target.value; navigate(()=>{modelOpenRequest.current++;modelLoadInFlight.current=false;setBusy(false);setRepositoryId(id);setDraft(null);setSnapshot(null);setBatches([]);setSelection([]);}); }}
                aria-label="Active repository"
              >
                {repositories.map((r) => (
                  <option key={r.repositoryId} value={r.repositoryId}>
                    {r.name}
                  </option>
                ))}
              </select>
              {session.permissions.includes("repository.create") && (
                <IconButton
                  label="Create repository"
                  icon={Plus}
                  onClick={() => setDialog("new-repository")}
                />
              )}
            </div>
            <div className="navigator-search search-input">
              <Search size={14} />
              <input
                placeholder="Find models or objects…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <kbd>⌕</kbd>
            </div>
            <div className="navigator-scroll">
              {sectionHeading("models", "Models", visibleModels.length)}
              <div id="explorer-models" hidden={!sectionExpanded("models")}>
              {visibleModels.map((m) => (
                <button
                  key={m.modelId}
                  data-model-id={m.modelId}
                  className={`model-tree-row ${draft?.model.modelId === m.modelId ? "active" : ""}`}
                  onClick={() => m.modelId !== draft?.model.modelId && navigate(() => void openModel(m.modelId))}
                  onContextMenu={(e) => {
                    e.preventDefault(); e.stopPropagation();
                    if (m.modelId === draft?.model.modelId) showNavigatorContext(e, { kind: "model", id: m.modelId });
                    else {
                      e.preventDefault();
                      const x = Math.min(e.clientX, window.innerWidth - 238), y = Math.min(e.clientY, window.innerHeight - 200);
                      navigate(() => { void openModel(m.modelId).then((opened) => opened && setContext({ x, y, id: m.modelId, kind: "model" })); });
                    }
                  }}
                  title={`${modelTypeLabel(m.modelType)} model`}
                >
                  <ModelTypeIcon type={m.modelType} size={17} />
                  <span>{m.name}</span>
                  <small>
                    {m.modelType === "physical"
                      ? "PHY"
                      : m.modelType === "conceptual"
                        ? "CON"
                        : m.modelType === "logical-physical" ? "L/P" : "LOG"}
                  </small>
                </button>
              ))}
              {!visibleModels.length && (
                <div className="small-empty">
                  {models.length
                    ? "No models match this view."
                    : "Your first model starts here."}
                </div>
              )}
              </div>
              {draft && (
                <>
                  {sectionHeading("diagrams", "Subject areas & views", draft.diagrams.length)}
                  <div id="explorer-diagrams" hidden={!sectionExpanded("diagrams")}>
                  {draft.diagrams.map(view => {const label=diagramExplorerLabel(draft,view);return (
                    <button key={view.diagramId} data-diagram-id={view.diagramId} className={`diagram-tree-row compact-diagram-row ${view.diagramId === activeDiagram?.diagramId ? "selected" : ""}`} title={label.fullText} aria-label={label.fullText} onClick={() => guardProperties(() => { setDiagramId(view.diagramId); setSelection([]); })} onContextMenu={(e) => showNavigatorContext(e, { kind: "diagram", id: view.diagramId })} onDoubleClick={() => openProperties({ kind: "diagram", id: view.diagramId })}>
                      <ModelTypeIcon type="group" size={16} /><span>{view.parentDiagramId ? "↳ " : ""}{label.name}</span><small className="diagram-context">{`{${label.context}}`}</small><small>{label.count}</small>
                    </button>
                  );})}
                  </div>
                  {sectionHeading("objects", "Objects", roots.length)}
                  <div id="explorer-objects" hidden={!sectionExpanded("objects")}>
                  {terms.physical && activeDiagram ? <PhysicalExplorer document={draft} representationId={activeDiagram.representationId} selection={selection} search={search} onSelect={id=>{selectObjects([id]);setRightPanel("inspector");}} onProperties={(kind,id)=>openProperties({kind,id})} onContext={(event,kind,id)=>showNavigatorContext(event,{kind,id})}/> : <>
                  {roots
                    .filter(
                      (o) =>
                        !search ||
                        o.name.toLowerCase().includes(search.toLowerCase()) ||
                        draft.objects.some(
                          (c) =>
                            c.ownerObjectId === o.objectId &&
                            c.name.toLowerCase().includes(search.toLowerCase()),
                        ),
                    )
                    .map((o) => {
                      const children = draft.objects.filter(
                        (c) => c.ownerObjectId === o.objectId,
                      );
                      return (
                        <div className="tree-branch" key={o.objectId}>
                          <div
                            className={`object-tree-row ${selection.includes(o.objectId) ? "selected" : ""}`}
                            onContextMenu={(e) => showNavigatorContext(e, { kind: "entity", id: o.objectId })}
                            onDoubleClick={() => openProperties({ kind: "entity", id: o.objectId })}
                          >
                            <button
                              className="tree-toggle"
                              aria-label={`${expanded[o.objectId] ? "Collapse" : "Expand"} ${o.name}`}
                              onClick={() =>
                                setExpanded({
                                  ...expanded,
                                  [o.objectId]: !expanded[o.objectId],
                                })
                              }
                            >
                              {children.length ? (
                                expanded[o.objectId] ? (
                                  <ChevronDown size={12} />
                                ) : (
                                  <ChevronRight size={12} />
                                )
                              ) : (
                                <span />
                              )}
                            </button>
                            <button
                              onClick={() => {
                                selectObjects([o.objectId]);
                                setRightPanel("inspector");
                              }}
                            >
                              <ModelTypeIcon type="entity" size={16} />
                              <span>{o.name}</span>
                              <small>
                                {
                                  children.filter((c) => c.kind !== "key")
                                    .length
                                }
                              </small>
                            </button>
                          </div>
                          {expanded[o.objectId] &&
                            children.map((child) => (
                              <button
                                key={child.objectId}
                                className={`field-tree-row ${selection.includes(child.objectId) ? "selected" : ""}`}
                                onDoubleClick={() => openProperties({ kind: "entity", id: child.objectId })}
                                onContextMenu={(e) => showNavigatorContext(e, { kind: "entity", id: child.objectId })}
                                onClick={() => {
                                  selectObjects([child.objectId]);
                                  setRightPanel("inspector");
                                }}
                              >
                                {child.kind === "key" ? (
                                  <KeyIcon />
                                ) : (
                                  <Columns3 size={12} />
                                )}
                                <span>{child.name}</span>
                                <small>
                                  {child.type?.name || child.keyRole || ""}
                                </small>
                              </button>
                            ))}
                        </div>
                      );
                    })}
                  </>}
                  </div>
                  {draft.relationships.length > 0 && (
                    <>
                      {sectionHeading("relationships", "Relationships", draft.relationships.length)}
                      <div id="explorer-relationships" hidden={!sectionExpanded("relationships")}>
                      {draft.relationships.map((r) => (
                        <button
                          className={`relationship-tree-row ${selection.includes(r.relationshipId) ? "selected" : ""}`}
                          key={r.relationshipId}
                          onContextMenu={(e) => showNavigatorContext(e, { kind: "relationship", id: r.relationshipId })}
                          onDoubleClick={() => openProperties({ kind: "relationship", id: r.relationshipId })}
                          onClick={() => {
                            selectObjects([r.relationshipId]);
                            setRightPanel("inspector");
                          }}
                        >
                          <GitBranch size={14} />
                          <span>{r.name}</span>
                        </button>
                      ))}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
            <div className="navigator-bottom">
              <span className="status-dot" />
              <span>{session.organizationCode}</span>
              <small>
                {canEdit ? "Modeler" : developer ? "Developer" : canDiagramEdit ? "Diagram editor" : "Read-only"}
              </small>
            </div>
          </aside>
        )}
        <main className="model-workspace">
          <div className="model-workspace-chrome">
          <DockableToolbar id="models" className="model-tabs" label="Open models" tabList>
            {!navOpen && !isFocus && (
              <IconButton
                label="Open explorer"
                icon={PanelLeftOpen}
                onClick={() => setNavOpen(true)}
              />
            )}
            <button
              className={`model-tab home-tab ${!draft ? "active" : ""}`}
              role="tab"
              aria-selected={!draft}
              onClick={() =>
                navigate(() => {
                  setDraft(null);
                  setSnapshot(null);
                  setBatches([]);
                })
              }
            >
              <LayoutGrid size={14} />
              <span>Overview</span>
            </button>
            {tabs.map((t) => (
              <div
                key={t.modelId}
                className={`model-tab ${draft?.model.modelId === t.modelId ? "active" : ""}`}
              >
                <button
                  role="tab"
                  aria-selected={draft?.model.modelId === t.modelId}
                  onClick={() =>
                    t.modelId !== draft?.model.modelId &&
                    navigate(() => void openModel(t.modelId))
                  }
                >
                  <ModelTypeIcon type={t.modelType || "logical"} size={16} />
                  <span>
                    {t.modelId === draft?.model.modelId
                      ? draft.model.name
                      : t.name}
                  </span>
                  {t.modelId === draft?.model.modelId && pendingCount > 0 && (
                    <span className="dirty-dot" />
                  )}
                </button>
                <button
                  className="close-tab"
                  aria-label={`Close ${t.name}`}
                  onClick={() => closeTab(t.modelId)}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            <IconButton
              label="Create a model"
              icon={Plus}
              disabled={!canCreate}
              title={
                !canCreate ? "Requires model.create permission" : undefined
              }
              onClick={() => navigate(() => setDialog("new-model"))}
            />
          </DockableToolbar>
          {draft && <div className="diagram-screen-controls" role="group" aria-label="Diagram screen controls">
                <IconButton label="Auto-fit displayed content" title="Auto-fit entity boxes to selected details; retain size locks" icon={ScanLine} disabled={!canDiagramEdit||busy} onClick={()=>setCanvasCommand({id:Date.now(),type:'auto-fit'})}/>
                <IconButton label="Compact diagram" title="Compact gaps and fit the diagram to the usable canvas" icon={Minimize2} disabled={!canDiagramEdit||busy} onClick={()=>setCanvasCommand({id:Date.now(),type:'compact'})}/>
                <IconButton label={isFocus ? "Restore panels" : "Maximize canvas"} icon={isFocus ? RestoreWorkspaceIcon : ExpandWorkspaceIcon} onClick={() => {
                  if (preferences.workspace === "focus") { void updatePreferences({workspace:"balanced"}); setFocus(false); }
                  else setFocus(!focus);
                }}/>
          </div>}
          </div>
          <DockSlot zone="tabs" />
          <DockSlot zone="diagram" />
          {draft ? (
            <>
              <DockableToolbar id="diagram" className="diagram-toolbar">
                <WorkspaceCommand id="active-diagram"><div className="diagram-title">
                  <select
                    aria-label="Active diagram"
                    value={activeDiagram?.diagramId || ""}
                    onChange={(e) => {
                      void flushPreferences().catch(()=>{});
                      guardProperties(() => { setDiagramId(e.target.value); setSelection([]); });
                    }}
                  >
                    {draft.diagrams.map((d) => (
                      <option key={d.diagramId} value={d.diagramId}>
                        {diagramExplorerLabel(draft,d).name}
                      </option>
                    ))}
                  </select>
                  <span className="tiny-tag">
                    {draft.representations.find(
                      (r) =>
                        r.representationId === activeDiagram?.representationId,
                    )?.origin || "original"}
                  </span>
                </div></WorkspaceCommand>
                <div className="toolbar-divider" />
                <WorkspaceCommand id="add-entity"><Button
                  icon={Table2}
                  disabled={!canEdit || !!historyRequest.current}
                  onClick={() => setAddObject({})}
                >
                  {terms.physical ? "Table" : terms.object === "collection" ? "Collection" : "Entity"}
                </Button></WorkspaceCommand>
                <WorkspaceCommand id="relationship"><Button
                  icon={RelationshipIcon}
                  disabled={!canEdit || !roots.length || !!historyRequest.current}
                  title={
                    !roots.length ? `Create a ${terms.object} first` : undefined
                  }
                  onClick={() => beginRelationship()}
                >
                  Relationship
                </Button></WorkspaceCommand>
                <WorkspaceCommand id="derive"><Button
                  icon={GitFork}
                  disabled={!canEdit || draft.model.family !== "relational"}
                  onClick={() => setDialog("transform")}
                >
                  Derive
                </Button></WorkspaceCommand>
                <WorkspaceCommand id="subject-view"><IconButton
                  label="Create subject view or independent copy"
                  icon={GitCompareArrows}
                  disabled={!canEdit || !roots.length}
                  onClick={() => setDialog("selection")}
                /></WorkspaceCommand>
                <div className="toolbar-divider" />
                <WorkspaceCommand id="notation"><select
                  className="notation-select"
                  aria-label="Diagram notation"
                  title="Relationship notation"
                  value={activeDiagram?.notation || preferences.notation}
                  disabled={!canDiagramEdit || !!historyRequest.current}
                  onChange={(e) =>
                    activeDiagram &&
                    apply(
                      [
                        {
                          type: "diagram.update",
                          diagramId: activeDiagram.diagramId,
                          changes: { notation: e.target.value },
                        },
                      ],
                      "Change diagram notation",
                    )
                  }
                >
                  <option value="crow-foot">Crow’s foot</option>
                  <option value="idef1x">IDEF1X</option>
                  <option value="uml">UML</option>
                  <option value="chen">Chen</option>
                </select></WorkspaceCommand>
                <div className="diagram-toolbar-spacer" />
                <WorkspaceCommand id="properties"><IconButton
                  className="mobile-inspector-toggle"
                  label="Toggle properties panel"
                  icon={SlidersHorizontal}
                  onClick={() => {
                    setDockOpen(!dockOpen);
                    setRightPanel("inspector");
                  }}
                /></WorkspaceCommand>
                <WorkspaceCommand id="typography"><TypographyTools preferences={preferences} locked={presentationLocks} terms={terms} onChange={updatePreferences} onFlush={()=>void flushPreferences().catch(()=>{})} /></WorkspaceCommand>
                <WorkspaceCommand id="appearance"><IconButton label="Diagram appearance" icon={Palette} disabled={!activeDiagram} onClick={() => activeDiagram && openAppearance({ kind: "diagram", id: activeDiagram.diagramId })} /></WorkspaceCommand>
                <WorkspaceCommand id="fit-view"><IconButton
                  label="Fit diagram to view"
                  icon={Maximize2}
                  onClick={() => runCanvas("fit")}
                /></WorkspaceCommand>

              </DockableToolbar>

              {remoteRevision > snapshot!.revision && (
                <div className="remote-banner">
                  <Bell size={15} />
                  <span>
                    Revision {remoteRevision} is available.
                    {pendingCount ? " Your local changes are preserved." : ""}
                  </span>
                  <button onClick={() => void receive(true)}>
                    Review incoming changes <ArrowRight size={13} />
                  </button>
                </div>
              )}
              <div className="canvas-area" onPointerDown={()=>setTemporaryPropertiesWidth(null)}>
                {activeDiagram ? (
                  <ModelCanvas
                    hideNotationControl
                    document={draft}
                    diagramId={activeDiagram.diagramId}
                    preferences={preferences}
                    selection={selection}
                    onSelection={selectObjects}
                    onViewportPlacement={registerCanvasPlacement}
                    onOperations={apply}
                    onOpenObject={(id) => openProperties({ kind: "entity", id })}
                    onOpenRelationship={(id) => openProperties({ kind: "relationship", id })}
                    onOpenAppearance={openAppearance}
                    onAddAttribute={(ownerId) => setAddObject({ ownerId })}
                    interactionBlocked={!!(viewRequest||dialog||addObject||addRelationship||propertyTarget||appearanceTarget||confirm||navigationGuard||navigationSaving||helpOverlayTopic)}
                    relationshipSource={relationshipSource}
                    relationshipPicking={relationshipPicking}
                    onCancelRelationship={cancelRelationship}
                    onBeginRelationship={beginRelationship}
                    onRelationshipTarget={pickRelationshipEntity}
                    lockedPreferences={presentationLocks}
                    onAddEntity={canEdit ? position => setAddObject({position}) : undefined}
                    onCreateSubjectArea={canEdit ? ids=>setViewRequest({mode:"subject-area",sourceDiagramId:activeDiagram.diagramId,selection:ids}) : undefined}
                    onCreateView={canEdit ? ids=>setViewRequest({mode:"view",sourceDiagramId:activeDiagram.diagramId,parentDiagramId:activeDiagram.role==="subject-area"?activeDiagram.diagramId:activeDiagram.parentDiagramId,selection:ids}) : undefined}
                    onAddEntities={canEdit && activeDiagram.role!=="main" ? ()=>setViewRequest({mode:"add",sourceDiagramId:activeDiagram.diagramId,targetDiagramId:activeDiagram.diagramId}) : undefined}
                    onImport={canCreate ? () => navigate(() => setDialog("import")) : undefined}
                    onBuildingBlock={canEdit ? () => setDialog("blocks") : undefined}
                    onPreferencesChange={(patch) => void updatePreferences(patch)}
                    onGestureActiveChange={markCanvasGesture}
                    canEditModel={canEdit}
                    onHelp={openHelp}
                    readOnly={busy || !canDiagramEdit || !!historyRequest.current}
                    command={canvasCommand}
                  />
                ) : (
                  <EmptyState
                    icon={Network}
                    title="This representation needs a diagram"
                  >
                    Choose an existing diagram or create one in the Views panel.
                  </EmptyState>
                )}
              </div>
              {diagnosticsOpen && (
                <section className="diagnostics-dock">
                  <div className="panel-heading">
                    <span>
                      MODEL DIAGNOSTICS <b>{diagnostics.length}</b>
                    </span>
                    <IconButton
                      label="Close diagnostics"
                      icon={X}
                      onClick={() => setDiagnosticsOpen(false)}
                    />
                  </div>
                  <Diagnostics
                    items={diagnostics}
                    onSelect={(id) => {
                      selectObjects([id]);
                      setRightPanel("inspector");
                    }}
                  />
                </section>
              )}
            </>
          ) : (
            <Overview
              session={session}
              models={models}
              repositories={repositories}
              onOpen={(id) => void openModel(id)}
              onCreate={() => setDialog("new-model")}
              onImport={() => setDialog("import")}
              onHelp={() => openHelp()}
              canEdit={canCreate}
            />
          )}
          <footer className="status-bar">
            <button
              onClick={() => setDiagnosticsOpen(!diagnosticsOpen)}
              disabled={!draft}
              className={errors ? "status-error" : ""}
            >
              {errors ? <AlertCircle size={13} /> : <CheckCircle2 size={13} />}
              <span>
                {errors
                  ? `${errors} errors`
                  : warnings
                    ? `${warnings} warnings`
                    : draft
                      ? "Model validated"
                      : "Workspace ready"}
              </span>
            </button>
            <span className="footer-divider" />
            <span className={`connection-status ${repositoryAccess.id===repositoryId?repositoryAccess.phase:"connecting"}`} role="status" title={repositoryAccess.name||activeRepo?.name}>
              <span className="status-dot" />
              <span>{!repositoryId?(serverReachable===true?"Server connected; select a repository":serverReachable===false?"Server unavailable":"Connecting to server"):`${repositoryAccess.id!==repositoryId?"Connecting to repository":({connecting:"Connecting to repository",connected:"Connected to repository",reconnecting:"Reconnecting to repository",unavailable:"Repository unavailable"} as const)[repositoryAccess.phase]}: ${repositoryAccess.id===repositoryId?repositoryAccess.name||activeRepo?.name||"selected repository":activeRepo?.name||"selected repository"}`}</span>
            </span>
            {draft&&<><span className="footer-divider"/><span className={`footer-save-state ${saveFailure?"failed":""}`}>{saving?"Saving":conflict||remoteRevision>0?"Conflict / incoming changes":saveFailure?"Save failed":pendingCount||inspectorDirty||propertyDirty?"Unsaved changes":recentlySaved?"Saved":"Up to date"}</span><span className="footer-divider"/><span className="live-state">Live updates: {connection==="online"?"connected":"reconnecting"}</span></>}
            <div className="status-spacer" />
            {draft && (
              <>
                <span>
                  {roots.length} {terms.objects} · {draft.relationships.length}{" "}
                  relationships
                </span>
                <span className="footer-divider" />
                <GitBranch size={12} />
                <span>main · r{snapshot?.revision}</span>
              </>
            )}
            <button onClick={() => openHelp("keyboard-shortcuts")}>
              Shortcuts <kbd>?</kbd>
            </button>
          </footer>
        </main>
        {draft && (
          <aside className={`right-dock panel-${rightPanel}`} style={{ display: isFocus || !dockOpen ? "none" : undefined, width:clampPanelWidth(temporaryPropertiesWidth ?? preferences.workspaceLayout.propertiesWidth ?? (rightPanel==="inspector"?306:350)*interfaceScale,viewportWidth,"properties") }}>
            <PanelResizeHandle side="properties" reversed={preferences.workspace==="inspector-left"} width={clampPanelWidth(temporaryPropertiesWidth ?? preferences.workspaceLayout.propertiesWidth ?? (rightPanel==="inspector"?306:350)*interfaceScale,viewportWidth,"properties")} onResize={setTemporaryPropertiesWidth}/ >
            <div className="panel-heading">
              <span>
                {{
                  inspector: "PROPERTIES",
                  help: "HELP CENTER",
                  assistant: "MODEL ASSISTANT",
                  perspective: "VIEWS & PERSPECTIVES",
                }[rightPanel] || "PROPERTIES"}
              </span>
              <div>
                {rightPanel === "inspector" && (
                  <IconButton
                    label="Preview naming policy"
                    icon={FileCode2}
                    disabled={!canEdit}
                    onClick={() => setDialog("naming")}
                  />
                )}
                <IconButton
                  label="Focus canvas"
                  icon={PanelRightClose}
                  onClick={() => setFocus(true)}
                />
              </div>
            </div>
            <div className={`persistent-inspector ${propertyTarget ? "inspector-suspended" : ""}`} inert={propertyTarget ? true : undefined} aria-disabled={!!propertyTarget} style={{ display: rightPanel === "inspector" ? undefined : "none" }}>
              <ObjectInspector
                key={`${draft.model.modelId}-${inspectorGeneration}`}
                document={draft}
                preferences={preferences}
                diagramId={activeDiagram?.diagramId}
                diagramReadOnly={!canDiagramEdit || !!historyRequest.current}
                selection={selection}
                readOnly={selectionReadOnly(selection) || !!propertyTarget}
                developer={developer}
                onOperations={apply}
                onSelection={selectObjects}
                onAddAttribute={(ownerId) => setAddObject({ ownerId })}
                onAddRelationship={(sourceId) => setAddRelationship({ sourceId })}
                onDelete={deleteObject}
                onHelp={openHelp}
                onDraftChange={markInspectorDirty} onRegisterSave={registerInspectorSave}
              />
            </div>
            {rightPanel === "assistant" ? (
              <AssistantPanel
                session={session}
                document={draft}
                onOperations={apply}
              />
            ) : rightPanel === "perspective" ? (
              <PerspectivePanel
                document={draft}
                representationId={activeDiagram?.representationId}
                diagramId={activeDiagram?.diagramId}
                onOperations={apply}
                readOnly={!canEdit}
              />
            ) : rightPanel === "help" ? (
              <HelpPanel topic={helpTopic} onTopicChange={setHelpTopic} />
            ) : null}
          </aside>
        )}
        {!draft && rightPanel === "help" && (
          <aside className="right-dock">
            <div className="panel-heading">
              <span>HELP CENTER</span>
              <IconButton
                icon={X}
                label="Close Help"
                onClick={() => setRightPanel("inspector")}
              />
            </div>
            <HelpPanel topic={helpTopic} onTopicChange={setHelpTopic} />
          </aside>
        )}
      </div>
      {toast && (
        <div
          className={`toast ${toast.kind}`}
          role={toast.kind === "error" ? "alert" : "status"}
        >
          {toast.kind === "error" ? (
            <AlertCircle size={17} />
          ) : toast.kind === "success" ? (
            <CheckCircle2 size={17} />
          ) : (
            <Bell size={17} />
          )}
          <span>{toast.text}</span>
          <IconButton
            label="Dismiss notification"
            icon={X}
            onClick={() => setToast(null)}
          />
        </div>
      )}
      {busy && (
        <div className="workspace-loading">
          <LoaderCircle className="spin" size={25} />
          <span>Opening model…</span>
        </div>
      )}
      {context && (
        <div ref={navigatorMenuRef} className="context-menu navigator-context-menu" role="menu" aria-label="Navigator actions" style={{ left: Math.max(8, context.x), top: Math.max(8, context.y), maxHeight: window.innerHeight - Math.max(8, context.y) - 12 }} onClick={event => event.stopPropagation()}>
          <strong className="navigator-context-title">{({explorer:"Explorer",models:"Models",diagrams:"Subject areas & views",objects:"Objects",relationships:"Relationships",model:"Model",diagram:"Subject area / view",entity:"Object",relationship:"Relationship",module:"Workspace actions"} as Record<string,string>)[context.kind]}</strong>
          {context.kind === "module" && <>
            {(context.id==="rail" ? [{id:"inspector",label:"Design"},{id:"perspective",label:"Views"},{id:"blocks",label:"Building blocks"},{id:"assistant",label:"Assistant"},{id:"settings",label:"Preferences"}] : [{id:context.id,label:({inspector:"Design properties",perspective:"Views",blocks:"Building blocks",assistant:"Assistant",help:"Help",admin:"Administration",settings:"Preferences"} as Record<string,string>)[context.id]||"Design"}]).map(item=><button key={item.id} role="menuitem" disabled={(item.id==="blocks"&&(!canEdit||!draft))||(["perspective","assistant"].includes(item.id)&&!draft)} onClick={()=>openModule(item.id)}><PanelLeftOpen size={14}/>Open {item.label}</button>)}
            {context.id==="perspective" && <><button role="menuitem" disabled={!canEdit||!activeDiagram} onClick={()=>requestView("subject-area")}><Plus size={14}/>Create subject area</button><button role="menuitem" disabled={!canEdit||!activeDiagram} onClick={()=>requestView("view")}><Plus size={14}/>Create view</button></>}
            {context.id==="inspector" && <button role="menuitem" disabled={!canEdit||!activeDiagram} onClick={()=>{setContext(null);setAddObject({});}}><Plus size={14}/>Create {terms.object}</button>}
            <button role="menuitem" onClick={()=>{setContext(null);updatePreferences({railLabels:!preferences.railLabels});}}><Eye size={14}/>{preferences.railLabels?"Hide":"Show"} navigation labels</button>
            <button role="menuitem" onClick={()=>openModule("settings")}><Settings2 size={14}/>Workspace preferences</button>
          </>}
          {(["explorer","models"] as string[]).includes(context.kind) && <>
            <button role="menuitem" disabled={!canCreate} onClick={() => {setContext(null);navigate(() => setDialog("new-model"));}}><Plus size={14}/>New model</button>
            <button role="menuitem" disabled={!canCreate} onClick={() => {setContext(null);navigate(() => setDialog("import"));}}><ArrowUpFromLine size={14}/>Import model</button>
            <button role="menuitem" disabled={refreshing} onClick={() => {setContext(null);void refreshLists().catch(e => notify(errorText(e),"error"));}}><RefreshCw size={14}/>Refresh model list</button>
          </>}
          {(["model","diagram","entity","relationship"] as string[]).includes(context.kind) && <>
            {context.kind === "diagram" && <button role="menuitem" onClick={() => {setContext(null);guardProperties(() => {setDiagramId(context.id);setSelection([]);});}}><Network size={14}/>Open view</button>}
            <button role="menuitem" onClick={() => openProperties({ kind: context.kind as AppearanceTarget["kind"], id: context.id })}><Settings2 size={14}/>Properties</button>
            <button role="menuitem" disabled={!canDiagramEdit} onClick={() => openAppearance({ kind: context.kind as AppearanceTarget["kind"], id: context.id })}><Palette size={14}/>{context.kind === "relationship" ? "Line & label appearance" : "Palette & appearance"}</button>
          </>}
          {(["model","diagram","diagrams"] as string[]).includes(context.kind) && <>
            <button role="menuitem" disabled={!canEdit || !contextDiagram || !!historyRequest.current} onClick={()=>requestView("subject-area",contextDiagram?.diagramId)}><Plus size={14}/>Create subject area</button>
            <button role="menuitem" disabled={!canEdit || !contextDiagram || !!historyRequest.current} onClick={()=>requestView("view",contextDiagram?.diagramId)}><Plus size={14}/>Create view</button>
            {contextDiagram?.role!=="main" && <button role="menuitem" disabled={!canEdit || !contextDiagram || !!historyRequest.current} onClick={()=>requestView("add",contextDiagram?.diagramId)}><Search size={14}/>Add entities...</button>}
            {contextDiagram?.role==="view" && <button role="menuitem" disabled={!canEdit || !!historyRequest.current} onClick={()=>{setContext(null);guardProperties(()=>{try{apply(promoteViewToSubjectArea(draft!,contextDiagram.diagramId),"Promote view to subject area");}catch(e){notify(errorText(e),"error");}});}}><GitCompareArrows size={14}/>Promote to subject area</button>}
            <button role="menuitem" disabled={!draft} onClick={() => {setContext(null);setRightPanel("perspective");setDockOpen(true);setFocus(false);}}><Layers3Icon size={14}/>Manage views</button>
          </>}
          {context.kind === "model" && <>
            <button role="menuitem" disabled={!canExport || !draft} onClick={() => {setContext(null);setDialog("export");}}><ArrowDownToLine size={14}/>Export model</button>
            <button role="menuitem" disabled={!draft} onClick={() => {setContext(null);setDialog("history");}}><History size={14}/>History</button>
            <button role="menuitem" disabled={!canEdit || draft?.model.family !== "relational"} onClick={() => {setContext(null);setDialog("transform");}}><GitFork size={14}/>Derive representation</button>
          </>}
          {context.kind === "objects" && <>
            <button role="menuitem" disabled={!canEdit || !!historyRequest.current || !activeDiagram} onClick={() => {setContext(null);guardProperties(() => setAddObject({}));}}><Plus size={14}/>Add {terms.object}</button>
            <button role="menuitem" disabled={!roots.length} onClick={() => {setContext(null);selectObjects(roots.map(o => o.objectId));}}><MousePointer2 size={14}/>Select all {terms.objects}</button>
          </>}
          {context.kind === "relationships" && <button role="menuitem" disabled={!canEdit || !roots.length || !!historyRequest.current} onClick={() => beginRelationship()}><GitBranch size={14}/>Create relationship</button>}
          {context.kind === "entity" && !draft?.objects.find(o => o.objectId === context.id)?.ownerObjectId && <>
            <button role="menuitem" disabled={!canAddField(context.id) || selectionReadOnly([context.id])} onClick={() => {setContext(null);guardProperties(() => setAddObject({ownerId:context.id}));}}><Plus size={14}/>Add {draft?.objects.find(o => o.objectId === context.id)?.kind === "table" ? "column" : "attribute"}</button>
            <button role="menuitem" disabled={!canEdit || selectionReadOnly([context.id])} onClick={() => beginRelationship(context.id)}><GitBranch size={14}/>Create relationship</button>
          </>}
          {(["models","diagrams","objects","relationships"] as string[]).includes(context.kind) && <button role="menuitem" onClick={() => {toggleSection(context.kind as ExplorerSection);setContext(null);}}>{sectionExpanded(context.kind as ExplorerSection) ? <Minus size={14}/> : <Plus size={14}/>} {sectionExpanded(context.kind as ExplorerSection) ? "Collapse section" : "Expand section"}</button>}
          {context.kind === "explorer" && <button role="menuitem" onClick={() => {setContext(null);updatePreferences({explorerSections:{models:true,diagrams:true,objects:true,relationships:true}});}}><Plus size={14}/>Expand Explorer sections</button>}
          <button role="menuitem" onClick={() => {setContext(null);openHelp(context.kind === "relationship" || context.kind === "relationships" ? "relationships" : context.kind === "diagram" || context.kind === "diagrams" ? "appearance" : "modeling");}}><CircleHelp size={14}/>Help</button>
          {(context.kind === "entity" || context.kind === "relationship") && <><hr/><button role="menuitem" disabled={!canEdit || selectionReadOnly([context.id])} className="danger" onClick={() => {setContext(null);guardProperties(() => deleteObject(context.id));}}><Trash2 size={14}/>Delete {context.kind === "relationship" ? "relationship" : "object"}</button></>}
        </div>
      )}
      {propertyTarget && draft && (propertyTarget.kind === "model" || propertyTarget.kind === "diagram") && (
        <WorkspacePropertiesDialog key={`${propertyTarget.kind}-${propertyTarget.id}-${inspectorGeneration}`} document={draft} target={propertyTarget} readOnly={busy || !canEdit || !!historyRequest.current} onApply={apply} onDirtyChange={markPropertyDirty} onRegisterSave={registerPropertySave} onClose={() => guardProperties(() => { setPropertyTarget(null); setPropertyDirty(false); })} onAppearance={() => openAppearance(propertyTarget)} onHelp={openHelp} />
      )}
      {propertyTarget && draft && (propertyTarget.kind === "entity" || propertyTarget.kind === "relationship") && (
        <Modal title="Properties" subtitle="Apply changes to the draft, then save the model." wide onClose={() => guardProperties(() => { setPropertyTarget(null); setPropertyDirty(false); })}>
          <div className="properties-popup" data-testid="properties-popup">
            <div className="properties-popup-actions"><Button icon={Palette} onClick={() => openAppearance(propertyTarget)}>Appearance</Button><Button icon={CircleHelp} onClick={() => openHelp(propertyTarget.kind === "relationship" ? "relationships" : "properties")}>Property help</Button></div>
            <ObjectInspector key={`${propertyTarget.id}-${inspectorGeneration}`} document={draft} preferences={preferences} diagramId={activeDiagram?.diagramId} diagramReadOnly={!canDiagramEdit || !!historyRequest.current} selection={propertyTarget.id ? [propertyTarget.id] : []} readOnly={selectionReadOnly(propertyTarget.id ? [propertyTarget.id] : [])} developer={developer} onOperations={apply} onSelection={(ids) => guardProperties(() => { setPropertyTarget({ kind: "entity", id: ids[0] }); setSelection(ids); })} onAddAttribute={(ownerId) => setAddObject({ ownerId })} onAddRelationship={(sourceId) => setAddRelationship({ sourceId })} onDelete={(id) => guardProperties(() => deleteObject(id))} onHelp={openHelp} onDraftChange={markPropertyDirty} onRegisterSave={registerPropertySave} />
          </div>
        </Modal>
      )}
      {appearanceTarget && draft && <AppearanceDialog document={draft} diagramId={appearanceTarget.kind === "diagram" ? appearanceTarget.id || diagramId : diagramId} target={appearanceTarget} preferences={preferences} onApply={apply} onClose={() => setAppearanceTarget(null)} onHelp={openHelp} readOnly={busy || !canDiagramEdit || !!historyRequest.current} />}
      {dialog === "new-model" && (
        <NewModelDialog
          repositories={repositories}
          initialRepository={repositoryId}
          onCreateRepository={
            session.permissions.includes("repository.create")
              ? () => setDialog("new-repository")
              : undefined
          }
          onClose={() => setDialog(null)}
          onCreate={createModel}
        />
      )}
      {dialog === "import" && (
        <ImportReviewDialog repositories={repositories} target={snapshot&&activeDiagram?{snapshot,diagramId:activeDiagram.diagramId,disabled:!canEdit||!!pendingCount||inspectorDirty||propertyDirty||saving}:undefined} onClose={() => setDialog(null)} onApplied={async (result,appended)=>{
          await refreshLists();
          const importedSnapshot=result.snapshot||result.receipt?.snapshot||(result.document?result:undefined);
          const id=importedSnapshot?.document?.model?.modelId||result.modelId;
          if(appended&&importedSnapshot){acceptSnapshot(importedSnapshot);} else if(id) await openModel(id); else throw new Error("Import completed without a model identifier.");
          setDialog(null);
        }}/>
      )}
      {dialog === "export" && draft && (
        <ExportDialog document={draft} diagramId={activeDiagram?.diagramId} selectedIds={selection} revision={snapshot?.revision} onClose={() => setDialog(null)} />
      )}
      {dialog === "blocks" && draft && (
        <BlocksDialog
          document={draft}
          onClose={() => setDialog(null)}
          onOperations={apply}
        />
      )}
      {dialog === "transform" && draft && (
        <TransformDialog
          document={draft}
          sourceRepresentationId={activeDiagram?.representationId}
          preferences={preferences}
          onClose={() => setDialog(null)}
          onOperations={apply}
        />
      )}
      {dialog === "naming" && draft && (
        <NamingDialog
          document={draft}
          onClose={() => setDialog(null)}
          onOperations={apply}
        />
      )}
      {dialog === "preferences" && (
        <PreferencesDialog
          preferences={preferences}
          onClose={() => setDialog(null)}
          document={draft??undefined}
          diagramId={activeDiagram?.diagramId}
          onChange={updatePreferences}
          onFlush={flushPreferences}
          saveState={preferenceSaveState}
          saveError={preferenceSaveError}
          onHelp={openHelp}
        />
      )}
      {dialog === "history" && draft && snapshot && (
        <HistoryDialog
          document={draft}
          baseline={snapshot.document}
          pendingCount={pendingCount}
          canPublish={canPublish}
          onClose={() => setDialog(null)}
          onPublished={() =>
            notify("Immutable publication created.", "success")
          }
        />
      )}
      {dialog === "admin" && (
        <Modal
          title="Studio administration"
          subtitle="Settings apply only within your delegated authority."
          wide
          onClose={() => setDialog(null)}
        >
          <AdminPanel
            session={session}
            onChanged={() => {
              api<any>("/auth/session").then((s) => onSession(s.session || s));
              void refreshLists();
            }}
          />
        </Modal>
      )}
      {dialog === "selection" && draft && snapshot && activeDiagram && (
        <SelectionDialog
          document={draft}
          diagramId={activeDiagram.diagramId}
          selection={selection}
          repositories={repositories}
          repositoryId={repositoryId}
          dirty={!!pendingCount || inspectorDirty || propertyDirty}
          canCreate={canCreate}
          sourceRevision={snapshot.revision}
          sourceCommitId={snapshot.commitId}
          onClose={() => setDialog(null)}
          onOperations={apply}
          onExtract={createModel}
        />
      )}
      {dialog === "database" && draft && (
        <Modal
          title="Database target"
          subtitle="Design-time target and support boundary."
          onClose={() => setDialog(null)}
          footer={
            <>
              <Button onClick={() => setDialog(null)}>Close</Button>
              <Button
                variant="primary"
                icon={FileCode2}
                onClick={() => setDialog("export")}
              >
                Inspect generated DDL
              </Button>
            </>
          }
        >
          <div className="database-target-card">
            <Database size={30} />
            <div>
              <span className="eyebrow">CURRENT TARGET</span>
              <h3>{dialectLabel(draft.model.targetProfile || "postgresql")}</h3>
            </div>
          </div>
          <Notice>
            Changing the target requires a reviewed derivation so type mappings
            and losses are explicit.
          </Notice>
          <Button
            icon={GitFork}
            disabled={!canEdit || draft.model.family !== "relational"}
            onClick={() => setDialog("transform")}
          >
            Derive for another target
          </Button>
          <div className="details-card">
            <h4>Live database connections</h4>
            <p className="subtle">
              Live discovery and execution are not available through this
              build’s browser API. Import a DDL file or export a reviewed
              script.
            </p>
          </div>
        </Modal>
      )}
      {dialog === "new-repository" && (
        <RepositoryDialog
          onClose={() => setDialog(null)}
          onCreate={async (data) => {
            const r: any = await api("/repositories", {
              method: "POST",
              body: JSON.stringify(data),
            });
            await refreshLists();
            setRepositoryId(r.repositoryId || r.repository?.repositoryId);
            setDialog(null);
          }}
        />
      )}
      {viewRequest && draft && <ViewMembershipDialog document={draft} {...viewRequest} onClose={()=>setViewRequest(null)} onOperations={apply}/>}
      {addObject && draft && (
        <ObjectDialog
          document={draft}
          preferences={preferences}
          diagramId={activeDiagram?.diagramId}
          ownerId={addObject.ownerId}
          position={addObject.position}
          onClose={() => setAddObject(null)}
          onCreate={(ops, id) => {
            if(!addObject.ownerId && activeDiagram && canvasPlacement.current){
              const projected=applyOperations(draft,ops).document;
              const projectedDiagram=projected.diagrams.find(d=>d.diagramId===activeDiagram.diagramId)!;
              const box=measuredDiagramBoxes(projected,projectedDiagram,preferences).find(b=>b.objectId===id);
              if(box){const position=canvasPlacement.current(box,addObject.position);ops=ops.map(op=>op.type==='appearance.create'&&op.diagramId===activeDiagram.diagramId?{...op,preservePosition:true,appearance:{...op.appearance!,position}}:op);}
            }
            if (!apply(ops, `Create ${addObject.ownerId ? "field" : "entity"}`))
              return;
            setSelection([id]);
            setRightPanel("inspector");
            setAddObject(null);
          }}
        />
      )}
      {addRelationship && draft && (
        <RelationshipDialog
          document={draft}
          diagramId={activeDiagram?.diagramId}
          sourceId={addRelationship.sourceId}
          targetId={addRelationship.targetId}
          representationId={
            activeDiagram?.representationId || draft.model.activeRepresentation
          }
          onClose={() => setAddRelationship(null)}
          onCreate={(ops, id) => {
            if (!apply(ops, "Create relationship")) return;
            setSelection([id]);
            setRightPanel("inspector");
            setAddRelationship(null);
          }}
        />
      )}
      {helpOverlayTopic && <Modal title="Context help" wide onClose={() => setHelpOverlayTopic(null)}><div className="context-help-popup"><HelpPanel topic={helpOverlayTopic} onTopicChange={setHelpOverlayTopic} /></div></Modal>}
      {navigationGuard && <Modal title="Save changes before continuing?" dismissible={!navigationSaving} onClose={() => { if(!navigationSaving) setNavigationGuard(null); }} footer={<div className="navigation-guard-actions">
        <Button disabled={navigationSaving} onClick={() => setNavigationGuard(null)}>Stay on this model</Button>
        <Button variant="danger" disabled={navigationSaving || !!saveCommand.current} title={saveCommand.current ? "Retry Save to resolve the unconfirmed command before discarding" : undefined} onClick={() => { const action=navigationGuard.action; discardPending(); setNavigationGuard(null); action(); }}>Discard changes and continue</Button>
        <Button variant="primary" icon={Save} busy={navigationSaving} disabled={navigationSaving || !canChange} onClick={() => {
          if(navigationInFlight.current) return;
          const action=navigationGuard.action; navigationInFlight.current=true; setNavigationSaving(true);
          void save().then(ok => { if(ok) { setNavigationGuard(null); action(); } }).finally(() => {navigationInFlight.current=false;setNavigationSaving(false);});
        }}>Save changes and continue</Button>
      </div>}><p>Your local model and property edits have not all been saved. Saving must succeed before continuing.</p></Modal>}
      {navigationSaving && !navigationGuard && <div className="navigation-save-status" role="status"><LoaderCircle size={16} className="spin"/>Saving before continuing...</div>}
      {confirm && (
        <Modal
          title={confirm.title}
          onClose={() => setConfirm(null)}
          footer={
            <>
              <Button onClick={() => setConfirm(null)}>Keep working</Button>
              <Button
                variant="danger"
                onClick={() => {
                  const action = confirm.action;
                  setConfirm(null);
                  action();
                }}
              >
                {confirm.label}
              </Button>
            </>
          }
        >
          <p>{confirm.message}</p>
        </Modal>
      )}
      {conflict && draft && conflictOpen && (
        <Modal
          title="Review incoming changes"
          subtitle={`Saved revision ${conflict.incoming.revision} is newer than your draft baseline.`}
          wide
          onClose={() => setConflict(null)}
          footer={
            <>
              <Button
                disabled={!canExport}
                title={
                  !canExport ? "Requires model.export permission" : undefined
                }
                onClick={() =>
                  downloadText(
                    `${draft.model.name}.local-draft.json`,
                    JSON.stringify(draft, null, 2),
                    "application/json",
                  )
                }
              >
                Export local draft
              </Button>
              <Button onClick={() => setConflict(null)}>
                Keep my draft open
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  setConfirm({
                    title: "Discard your draft and receive?",
                    message:
                      "Your local operations and retained form edits will be discarded. Export your draft first if you need a copy.",
                    label: "Discard & receive",
                    action: () => {
                      acceptSnapshot(conflict.incoming);
                      setConflict(null);
                    },
                  });
                }}
              >
                Discard draft & receive
              </Button>
            </>
          }
        >
          <Notice kind="warning">
            Conflicting changes are never silently overwritten. Keep or export
            your draft, then reconcile deliberately.
          </Notice>
          {relationshipCollisionResolution&&<section className="relationship-collision-resolution"><p>These names became unavailable while the draft was open. Accept the replacements below to keep your other edits and rebase onto the saved revision.</p><ul>{relationshipCollisionResolution.proposals.map(item=><li key={item.id}>{item.before} → {item.after}</li>)}</ul><Button onClick={applyRelationshipCollisionResolution}>Accept proposed names and retain draft</Button></section>}
          {(inspectorDirty || propertyDirty) && (
            <p>
              Unapplied inspector edits are also preserved. Apply or discard
              those before receiving a compatible update.
            </p>
          )}
          {conflict.conflicts.map((c, i) => (
            <div className="conflict-row" key={i}>
              <strong>{c.message || c.path}</strong>
              <code>{c.path}</code>
              <div className="conflict-values">
                <div>
                  <span>Your draft</span>
                  <pre>{JSON.stringify(c.local, null, 2)}</pre>
                </div>
                <div>
                  <span>Incoming revision</span>
                  <pre>{JSON.stringify(c.incoming, null, 2)}</pre>
                </div>
              </div>
            </div>
          ))}
        </Modal>
      )}
    </div>
    </WorkspaceToolsProvider>
  );
}
function ExpandWorkspaceIcon({size=18}: {size?:number}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 9 3 3m0 5V3h5M15 9l6-6m-5 0h5v5M9 15l-6 6m5 0H3v-5M15 15l6 6m0-5v5h-5"/></svg>;
}
function RestoreWorkspaceIcon({size=18}: {size?:number}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m3 3 6 6M4 9h5V4m12-1-6 6m0-5v5h5M3 21l6-6m-5 0h5v5m12 1-6-6m0 5v-5h5"/></svg>;
}
function LayersIcon(props: any) {
  return <Layers3Icon {...props} />;
}
function Layers3Icon({ size = 18 }: any) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m12 3 10 6-10 6L2 9l10-6Zm-9 11 9 5 9-5M3 18l9 5 9-5" />
    </svg>
  );
}
function Overview({
  session,
  models,
  repositories,
  onOpen,
  onCreate,
  onImport,
  onHelp,
  canEdit,
}: {
  session: Session;
  models: ModelSummary[];
  repositories: Repository[];
  onOpen: (id: string) => void;
  onCreate: () => void;
  onImport: () => void;
  onHelp: () => void;
  canEdit: boolean;
}) {
  return (
    <div className="overview">
      <div className="overview-top">
        <div>
          <span className="eyebrow">YOUR MODELING WORKSPACE</span>
          <h1>
            Good design starts
            <br />
            with a clear picture.
          </h1>
          <p>Connect business meaning to the systems that hold it.</p>
        </div>
        <div className="overview-mark">
          <Network size={100} strokeWidth={0.7} />
        </div>
      </div>
      <div className="quick-start-grid">
        <button disabled={!canEdit} onClick={onCreate}>
          <span className="quick-icon">
            <FilePlus2 size={23} />
          </span>
          <div>
            <h3>Create a model</h3>
            <p>Start from an empty canvas.</p>
          </div>
          <ArrowRight size={17} />
        </button>
        <button disabled={!canEdit} onClick={onImport}>
          <span className="quick-icon blue">
            <ArrowUpFromLine size={23} />
          </span>
          <div>
            <h3>Bring in a schema</h3>
            <p>Inspect and import DDL or JSON.</p>
          </div>
          <ArrowRight size={17} />
        </button>
        <button onClick={onHelp}>
          <span className="quick-icon ochre">
            <BookOpen size={23} />
          </span>
          <div>
            <h3>Find your way</h3>
            <p>Practical, context-aware guidance.</p>
          </div>
          <ArrowRight size={17} />
        </button>
      </div>
      <div className="recent-heading">
        <h2>
          Recent models <span>{models.length}</span>
        </h2>
        <span>
          {repositories.length}{" "}
          {repositories.length === 1 ? "repository" : "repositories"}
        </span>
      </div>
      {models.length ? (
        <div className="models-table">
          <div className="models-table-head">
            <span>MODEL</span>
            <span>TYPE</span>
            <span>REPOSITORY</span>
            <span>LAST UPDATED</span>
          </div>
          {models
            .slice()
            .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
            .map((m) => (
              <button key={m.modelId} onClick={() => onOpen(m.modelId)}>
                <span className="table-model-name">
                  <span className="table-model-icon">
                    <ModelTypeIcon type={m.modelType} size={21} />
                  </span>
                  <strong>{m.name}</strong>
                </span>
                <span>
                  <span className="model-type-tag">
                    {modelTypeLabel(m.modelType)}
                  </span>
                </span>
                <span>
                  {repositories.find((r) => r.repositoryId === m.repositoryId)
                    ?.name || "Repository"}
                </span>
                <span>
                  {new Date(m.updatedAt).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                  })}
                  <ChevronRight size={15} />
                </span>
              </button>
            ))}
        </div>
      ) : (
        <div className="empty-library">
          <div>
            <Folder size={34} strokeWidth={1.2} />
            <h3>Your workspace is ready.</h3>
            <p>
              Create your first model or import an existing schema.
              <br />
              There are no sample models to clean up.
            </p>
            <Button
              variant="primary"
              icon={Plus}
              disabled={!canEdit}
              onClick={onCreate}
            >
              Create your first model
            </Button>
          </div>
        </div>
      )}
      <div className="overview-note">
        <Shield size={15} />
        <span>
          Drafts, saved revisions, publications, and database deployments remain
          distinct.
        </span>
        <span className="alpha-note">
          Development build · verify support per profile
        </span>
      </div>
    </div>
  );
}
function RepositoryDialog({
  onClose,
  onCreate,
}: {
  onClose: () => void;
  onCreate: (data: any) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  return (
    <Modal
      title="Create a repository"
      subtitle="Keep related models together within your studio."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!name.trim()}
            busy={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onCreate({
                  name,
                  description,
                  mapping: { kind: "unmapped" },
                });
              } catch (e) {
                setError(errorText(e));
                setBusy(false);
              }
            }}
          >
            Create repository
          </Button>
        </>
      }
    >
      <Field label="Repository name">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Enterprise architecture"
        />
      </Field>
      <Field label="Description">
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
        />
      </Field>
      {error && <Notice kind="error">{error}</Notice>}
    </Modal>
  );
}
function ObjectDialog({
  document,
  preferences,
  diagramId,
  ownerId,
  position,
  onClose,
  onCreate,
}: {
  document: ModelDocument;
  preferences: Preferences;
  diagramId?: string;
  ownerId?: string;
  position?: {x:number;y:number};
  onClose: () => void;
  onCreate: (ops: Operation[], id: string) => void;
}) {
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [nullable, setNullable] = useState(true);
  const [description, setDescription] = useState("");
  const owner = document.objects.find((o) => o.objectId === ownerId);
  const diagram = document.diagrams.find((d) => d.diagramId === diagramId);
  const repr = document.representations.find(
    (r) =>
      r.representationId ===
      (owner?.representationId ||
        diagram?.representationId ||
        document.model.activeRepresentation),
  );
  const words = vocabulary(document, repr?.representationId, owner, diagram?.perspective);
  const physical = words.physical;
  const kind: DomainObject["kind"] = ownerId
    ? document.model.family === "document"
      ? "property"
      : document.model.family === "semantic"
        ? "semanticField"
        : physical
          ? "column"
          : "attribute"
    : document.model.family === "document"
      ? "collection"
      : document.model.family === "semantic"
        ? "semanticEntity"
        : document.model.family === "analytical"
          ? "fact"
          : physical
            ? "table"
            : repr?.kind === "conceptual"
              ? "concept"
              : "entity";
  const create = () => {
    const object = {
      ...createObject(
        kind,
        name.trim(),
        repr?.representationId || document.model.activeRepresentation,
        ownerId,
      ),
      description,
      ...(ownerId
        ? {
            ...(physical ? {type:{name:type || "VARCHAR"}} : words.conceptual ? {} : {logicalType:type || "string"}),
            nullable,
            ordinal: document.objects.filter((o) => o.ownerObjectId === ownerId)
              .length,
          }
        : {}),
    };
    const ops: Operation[] = [{ type: "object.create", object }, ...(!ownerId ? defaultEntityFields(document, object, preferences) : [])];
    if (!ownerId && diagram) {
      const count = diagram.appearances.length;
      ops.push({
        type: "appearance.create",
        diagramId: diagram.diagramId,
        appearance: {
          appearanceId: uid(),
          objectId: object.objectId,
          position: position || {
            x: 80 + (count % 3) * 330,
            y: 80 + Math.floor(count / 3) * 280,
          },
          width: 260,
          sizeLocked: false,
        },
      });
    }
    onCreate(ops, object.objectId);
  };
  return (
    <Modal
      title={
        ownerId
          ? `Add ${physical ? "a column" : "an attribute"} to ${owner?.name}`
          : `Create ${kind === "table" ? "a table" : kind === "collection" ? "a collection" : "an entity"}`
      }
      subtitle={
        ownerId
          ? "Describe a single property of this object."
          : "Start with a name and meaning. Define fields and keys as you go."
      }
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            icon={Plus}
            variant="primary"
            disabled={!name.trim()}
            onClick={create}
          >
            Add to draft
          </Button>
        </>
      }
    >
      <Field label={ownerId ? "Field name" : "Name"}>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && name.trim()) create();
          }}
          placeholder={ownerId ? "e.g. customer_id" : "e.g. Customer"}
        />
      </Field>
      <Field label="Description">
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          placeholder="What does this mean to the business?"
        />
      </Field>
      {ownerId && (
        <>
          {!words.conceptual && <Field label={physical ? "Data type" : "Logical data type"}>
            <input
              list="new-field-types"
              value={type || (physical ? "VARCHAR" : "string")}
              onChange={(e) => setType(e.target.value)}
            />
            <datalist id="new-field-types">
              {(physical ? [
                "varchar",
                "text",
                "integer",
                "bigint",
                "uuid",
                "boolean",
                "date",
                "timestamp",
                "decimal",
                "jsonb",
              ] : ["string", "identifier", "integer", "decimal", "date", "boolean"]).map((t) => (
                <option key={t}>{t}</option>
              ))}
            </datalist>
          </Field>}
          <label className="check-row">
            <input
              type="checkbox"
              checked={nullable}
              onChange={(e) => setNullable(e.target.checked)}
            />
            Allow null values
          </label>
        </>
      )}
    </Modal>
  );
}
function RelationshipDialog({
  document,
  sourceId,
  targetId,
  representationId,
  diagramId,
  onClose,
  onCreate,
}: {
  document: ModelDocument;
  diagramId?: string;
  sourceId?: string;
  targetId?: string;
  representationId: string;
  onClose: () => void;
  onCreate: (ops: Operation[], id: string) => void;
}) {
  const roots = document.objects.filter(
    (o) => !o.ownerObjectId && o.representationId === representationId,
  );
  const [source, setSource] = useState(
    roots.some((o) => o.objectId === sourceId)
      ? sourceId!
      : roots[0]?.objectId || "",
  );
  const [target, setTarget] = useState(
    roots.some(o => o.objectId === targetId) ? targetId! : roots.find((o) => o.objectId !== source)?.objectId || roots[0]?.objectId || "",
  );
  const suggestion = relationshipSuggestion(document, representationId, source, target);
  const [name, setName] = useState(suggestion.name);
  const [description, setDescription] = useState(suggestion.description);
  const [nameEdited, setNameEdited] = useState(false), [descriptionEdited, setDescriptionEdited] = useState(false);
  const terms = vocabulary(document, representationId, undefined, document.diagrams.find(d=>d.diagramId===diagramId)?.perspective);
  useEffect(() => { if (!nameEdited) setName(suggestion.name); if (!descriptionEdited) setDescription(suggestion.description); }, [suggestion.name, suggestion.description, nameEdited, descriptionEdited]);
  const [selfReference,setSelfReference]=useState(false);
  const [sourceOptional,setSourceOptional]=useState(false);
  const [labelSource,setLabelSource]=useState<"name"|"description">("name");
  const [error,setError]=useState("");
  const collision=document.relationships.some(r=>r.representationId===representationId&&r.name.normalize("NFKC").toLowerCase()===name.trim().normalize("NFKC").toLowerCase());
  const endpointsValid=roots.some(o=>o.objectId===source)&&roots.some(o=>o.objectId===target);
  const valid=endpointsValid&&!!name.trim()&&name.trim().length<=512&&!collision&&(source!==target||selfReference);
  const submit=()=>{
    setError("");
    if(!valid){setError("Review both endpoints, the relationship name and any self-reference before adding.");return;}
    const id=uid();
    const operations:Operation[]=[{type:"relationship.create",relationship:{relationshipId:id,name:name.trim(),description:description.trim(),representationId,sourceObjectId:source,targetObjectId:target,sourceCardinality:cardinality.split("-")[0] as "one"|"many",targetCardinality:cardinality.split("-")[1] as "one"|"many",sourceOptional,targetOptional:optional,identifying,extensions:{suggestionProvider:RELATIONSHIP_SUGGESTION_VERSION}}}];
    if(diagramId&&labelSource!=="name")operations.push({type:"connector.set",diagramId,connector:{relationshipId:id,style:{label:{source:labelSource}}}});
    try{onCreate(operations,id);}catch(e){setError(errorText(e));}
  };
  const [cardinality, setCardinality] = useState("one-many");
  const [optional, setOptional] = useState(false);
  const [identifying, setIdentifying] = useState(false);
  return (
    <Modal
      title={`Connect two ${terms.objects}`}
      subtitle="Review source and target roles, cardinality and participation. Click order does not establish business parenthood."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            icon={GitBranch}
            variant="primary"
            disabled={!valid}
            onClick={submit}
          >
            Add relationship
          </Button>
        </>
      }
    >
      <div className="form-row">
        <Field label={`Source ${terms.object}`}>
          <select value={source} onChange={(e) => setSource(e.target.value)}>
            {roots.map((o) => (
              <option key={o.objectId} value={o.objectId}>
                {o.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={`Target ${terms.object}`}>
          <select value={target} onChange={(e) => setTarget(e.target.value)}>
            {roots.map((o) => (
              <option key={o.objectId} value={o.objectId}>
                {o.name}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Relationship name">
        <input
          value={name}
          onChange={(e) => { setName(e.target.value); setNameEdited(true); }}
          maxLength={512}
          placeholder="e.g. Customer_Order"
        />
        <div className="relationship-field-actions"><small>{nameEdited?"Your name":"Suggested name"}</small><Button onClick={()=>{setNameEdited(false);setName(suggestion.name);}}>Use suggested name</Button></div>
      </Field>
      <Field label="Relationship description" hint="Suggested from the endpoints. Refine its business meaning; the caption can use this description.">
        <textarea value={description} onChange={e => { setDescription(e.target.value); setDescriptionEdited(true); }} rows={2} />
        <div className="relationship-field-actions"><small>{descriptionEdited?"Your description":"Suggested description"}</small><Button onClick={()=>{setDescriptionEdited(false);setDescription(suggestion.description);}}>Use suggested description</Button></div>
      </Field>
      {collision&&<Notice kind="warning">This relationship name is already used. Your input is retained. Use suggested name to choose {suggestion.name} or enter another name.</Notice>}
      {error&&<Notice kind="error">{error}</Notice>}
      <div className="form-row"><Button onClick={() => { setSource(target); setTarget(source); }}>Swap endpoints</Button></div>
      {source===target&&<label className="check-row"><input type="checkbox" checked={selfReference} onChange={e=>setSelfReference(e.target.checked)}/>Create an intentional self-reference on this {terms.object}</label>}
      <Field label="Relationship label source"><select value={labelSource} onChange={e=>setLabelSource(e.target.value as "name"|"description")}><option value="name">Name</option><option value="description">Description</option></select><div className="relationship-label-preview" aria-label="Relationship label preview">{labelSource==="description"&&description.trim()?description.trim():name.trim()||"Enter a relationship name"}</div>{labelSource==="description"&&!description.trim()&&<small>Empty description falls back to the name.</small>}</Field>
      <Field label="Cardinality">
        <select
          value={cardinality}
          onChange={(e) => setCardinality(e.target.value)}
        >
          <option value="one-many">One to many</option>
          <option value="one-one">One to one</option>
          <option value="many-many">Many to many</option>
          <option value="many-one">Many to one</option>
        </select>
      </Field>
      <label className="check-row">
        <input
          type="checkbox"
          checked={optional}
          onChange={(e) => setOptional(e.target.checked)}
        />
        Target participation is optional
      </label>
      <label className="check-row"><input type="checkbox" checked={sourceOptional} onChange={e=>setSourceOptional(e.target.checked)}/>Source participation is optional</label>
      <label className="check-row">
        <input
          type="checkbox"
          checked={identifying}
          onChange={(e) => setIdentifying(e.target.checked)}
        />
        Identifying relationship
      </label>
      <Notice>
        After creation, review the field mapping in relationship properties
        before generating physical constraints.
      </Notice>
    </Modal>
  );
}
